/**
 * IngestionModal — multi-step document ingestion wizard.
 *
 * Step flow: parsing → [sheet_select] → chooser → column_mapping → extracting → diff mode
 *
 * The chooser step offers three modes (plus a scope selector):
 * - Import Changes: detect diffs between file and DB, review in PivotTable diff mode
 * - Export Changes: write DB values into document where they differ
 * - Select Records: filter table by document content
 *
 * Anchor (row↔record matching) runs at extraction time using the user-confirmed
 * PK column from the mapping step — not before.
 */

import {
  useTranslation,
  type TranslationKeys,
} from "@/hooks/useTranslation/useTranslation"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import clsx from "clsx"
import { useEffect, useRef, useState } from "react"
import { api } from "../../api/client"
import { executeIngestionTemplate } from "../../lib/templates/executor"
import {
  getIngestionTemplate,
  listIngestionTemplatesForAnchor,
} from "../../lib/templates/registry"
import { useStore } from "../../store/useStore"
import type {
  AnchoredRecord,
  AnchorMatch,
  AnchorOut,
  BuildHierarchyOut,
  DetectConnectedMatch,
  DetectOut,
  DiffRecord,
  DiffState,
  ExtractionEntry,
  ImplicitPrimitiveCandidate,
  NearMissWarning,
  NewRowCandidate,
  ParsedDocument,
  ParsedSheet,
  PrimitiveDef,
  PrimitiveType,
  RecordSelection,
  RecordsPage,
  RelationDiff,
  RelationDiffEntry,
  RollupColumnDef,
  SelectMapping,
  UnconnectedSummary,
  UserMapping,
} from "../../types"
import {
  evaluateFormula,
  extractReferences,
  type TranslateFn,
} from "../../utils/formula"
import { FreshnessDatePicker } from "../FreshnessDatePicker"
import {
  checkTypeIncompatible,
  ColumnMappingStep,
  type AnchorChoice,
} from "./ColumnMappingStep"
import { HierarchyPreview } from "./HierarchyPreview"

// ---------------------------------------------------------------------------
// Mapping memory — persists confirmed column→primitive mappings across cascades
// ---------------------------------------------------------------------------

type ScopeMappingMemory = {
  pkColIdx: number | null
  pkPrimitiveId: string | null
  pkPrimitiveTitle: string
  pkPrimitiveType: string
  mappings: UserMapping[]
}

const MAPPING_MEMORY_KEY = "qms_mapping_memory"

function loadMappingMemory(): Record<string, ScopeMappingMemory> {
  try {
    return JSON.parse(localStorage.getItem(MAPPING_MEMORY_KEY) ?? "{}")
  } catch {
    return {}
  }
}

function saveMappingMemory(memory: Record<string, ScopeMappingMemory>) {
  try {
    localStorage.setItem(MAPPING_MEMORY_KEY, JSON.stringify(memory))
  } catch {
    /* empty */
  }
}

/**
 * Evaluate helper (formula) mappings per anchored record and push their
 * computed values into the extraction stream so the diff view and the
 * downloaded file both see them.  Helpers are topologically sorted by their
 * dependencies on each other; cycles surface as null values with an error
 * string (rendered inline in the diff view).
 *
 * Not persisted to the DB — commit filters them out by synthetic primitive id
 * (the backend also treats unknown primitive_ids as no-ops).
 */
function appendHelperExtractions({
  mappings,
  anchored,
  cachedPage,
  rows,
  headers,
  extractions,
  translate,
}: {
  mappings: UserMapping[]
  anchored: AnchoredRecord[]
  cachedPage:
    | {
        records: {
          id: string
          values: Record<string, { value: string | null }>
        }[]
      }
    | undefined
  rows: string[][]
  headers: string[]
  extractions: ExtractionEntry[]
  translate: TranslateFn
}): void {
  const helpers = mappings.filter((m) => m.isHelper && m.helperConfig)
  if (helpers.length === 0) return

  const helperByLabel = new Map(helpers.map((h) => [h.helperConfig!.label, h]))

  for (const a of anchored) {
    if (a.is_new) continue // new records have no DB values yet; skip helper computation
    const row =
      a.row_index != null && a.row_index < rows.length ? rows[a.row_index] : []
    const cachedRec = cachedPage?.records.find((r) => r.id === a.record_id)
    const docCells: Record<string, string> = {}
    for (const h of headers) {
      const idx = headers.indexOf(h)
      docCells[h] = idx >= 0 ? (row[idx] ?? "").trim() : ""
    }
    const results: Record<string, string | null> = {}
    const resolvedLabels = new Set<string>()
    let changed = true
    while (changed) {
      changed = false
      for (const h of helpers) {
        const label = h.helperConfig!.label
        if (resolvedLabels.has(label)) continue
        const refs = extractReferences(h.helperConfig!.expression)
        const blocked = refs.identifiers.some(
          (id) =>
            helperByLabel.has(id) && !resolvedLabels.has(id) && id !== label,
        )
        if (blocked) continue
        const identifiers: Record<string, string> = {}
        for (const id of refs.identifiers) {
          if (helperByLabel.has(id)) {
            identifiers[id] = results[helperByLabel.get(id)!.primitive_id] ?? ""
          } else {
            identifiers[id] = cachedRec?.values[id]?.value ?? ""
          }
        }
        const result = evaluateFormula(
          h.helperConfig!.expression,
          { identifiers, docCells },
          translate,
        )
        results[h.primitive_id] = result.value
        resolvedLabels.add(label)
        changed = true
        // When the helper is assigned to a doc header, route its output to
        // that column so the download path replaces the existing cell.
        const targetHdr = h.helperConfig!.targetDocHeader
        const targetIdx = targetHdr ? headers.indexOf(targetHdr) : -1
        // Capture the current doc-cell value (if any) so the diff view can
        // tell whether the helper is writing into empty space (additive →
        // GREEN) or overwriting existing content (destructive → RED).  This
        // doubles as the fallback we preserve if the user declines the
        // helper's value.
        const existingDocVal =
          targetIdx >= 0 ? (row[targetIdx] ?? "").trim() : ""
        extractions.push({
          record_id: a.record_id,
          primitive_id: h.primitive_id,
          primitive_scope_id: h.primitive_scope_id,
          primitive_title: h.helperConfig!.label,
          primitive_type: "formula",
          value: result.value,
          raw_value: result.error ?? null,
          needs_cleaning: false,
          confidence: 1,
          extraction_type: "explicit",
          accepted: true,
          ...(targetIdx >= 0 ? { export_column_index: targetIdx } : {}),
          ...(existingDocVal ? { existing_value: existingDocVal } : {}),
        })
      }
    }
    // Any helper still unresolved — surface a cycle error entry.
    for (const h of helpers) {
      if (resolvedLabels.has(h.helperConfig!.label)) continue
      extractions.push({
        record_id: a.record_id,
        primitive_id: h.primitive_id,
        primitive_scope_id: h.primitive_scope_id,
        primitive_title: h.helperConfig!.label,
        primitive_type: "formula",
        value: null,
        raw_value: "Circular reference or missing dependency",
        needs_cleaning: true,
        confidence: 0,
        extraction_type: "explicit",
        accepted: false,
      })
    }
  }
}

/**
 * Parse an anchor-endpoint error into a user-visible German message.
 * Ambiguous / unresolved mapping IDs become blocking errors with a
 * placeholder note — they will eventually route into the planned
 * "Konflikte beheben" resolve-issues step.
 */
// TODO: when the "Konflikte beheben" step exists, replace these bail-out
// error messages with a routed state that lets the user fix values inline.
function formatAnchorError(
  e: unknown,
  translate: (
    k: TranslationKeys,
    opts?: Record<string, string | number>,
  ) => string,
): string {
  const raw = e instanceof Error ? e.message : String(e)
  // request() throws "API <status>: <body>"; peel the body off and try JSON.
  const bodyStart = raw.indexOf(": ")
  const body = bodyStart >= 0 ? raw.slice(bodyStart + 2) : raw
  try {
    const parsed = JSON.parse(body)
    const detail = parsed?.detail ?? parsed
    if (detail && typeof detail === "object") {
      if (detail.message_de) return String(detail.message_de)
      if (detail.code === "AMBIGUOUS_MAPPING_ID" && detail.value) {
        return translate("QMS/INGESTION/AMBIGUOUS_LINK", {
          value: detail.value,
        })
      }
      if (detail.code === "UNRESOLVED_MAPPING_ID" && detail.value) {
        return translate("QMS/INGESTION/LINK_NOT_FOUND", {
          value: detail.value,
        })
      }
    }
  } catch {
    /* fall through to raw */
  }
  return translate("QMS/INGESTION/ANCHOR_FAILED", { raw })
}

// ---------------------------------------------------------------------------
// Step discriminated union
// ---------------------------------------------------------------------------

type Step =
  | { id: "parsing" }
  | { id: "sheet_select"; parsed: ParsedDocument }
  | { id: "chooser"; parsed: ParsedDocument; activeSheet: ParsedSheet | null }
  | {
      id: "column_mapping"
      parsed: ParsedDocument
      activeSheet: ParsedSheet | null
      detected: DetectOut
      pkPrimitiveId: string | null
      pkPrimitiveTitle: string
      pkPrimitiveType: string
    }
  | {
      id: "extracting"
      parsed: ParsedDocument
      activeSheet: ParsedSheet | null
      mappings: UserMapping[]
      pkPrimitiveId: string | null
      anchorChoice?: AnchorChoice
    }
  | {
      id: "select_mapping"
      parsed: ParsedDocument
      activeSheet: ParsedSheet | null
      pkValues: string[]
      nearMisses: NearMissWarning[]
      matches: DetectConnectedMatch[]
      anchorMatches: AnchorMatch[]
      anchorOut: AnchorOut
    }

const STEP_ORDER = ["parsing", "chooser", "column_mapping", "extracting"]
function getStepLabels(
  translate: (key: TranslationKeys) => string,
): Record<string, string> {
  return {
    parsing: translate("QMS/INGESTION/STEP_PARSING"),
    sheet_select: translate("QMS/INGESTION/STEP_PARSING"),
    chooser: translate("QMS/INGESTION/STEP_CHOOSER"),
    column_mapping: translate("QMS/INGESTION/STEP_COLUMN_MAPPING"),
    extracting: translate("QMS/INGESTION/STEP_EXTRACTING"),
    select_mapping: translate("QMS/INGESTION/STEP_SELECT"),
  }
}

// ---------------------------------------------------------------------------
// Main modal
// ---------------------------------------------------------------------------

export function IngestionModal() {
  const { translate } = useTranslation()
  const {
    ingestion,
    closeIngestion,
    enterDiffMode,
    recordSelection,
    setRecordSelection,
    addSameScopePks,
    addCrossScopePkSet,
    setRollupState,
  } = useStore()
  const qc = useQueryClient()

  const [step, setStep] = useState<Step>({ id: "parsing" })
  const [error, setError] = useState<string | null>(null)
  const [anchorPkPrimId, setAnchorPkPrimId] = useState<string | null>(null)
  const [anchorPkPrimTitle, setAnchorPkPrimTitle] = useState<string>("")
  const [anchorPkPrimType, setAnchorPkPrimType] = useState<string>("string")
  const [anchorDetected, setAnchorDetected] = useState<DetectOut | null>(null)
  const [parsedDoc, setParsedDoc] = useState<ParsedDocument | null>(null)
  const [activeSheetState, setActiveSheetState] = useState<ParsedSheet | null>(
    null,
  )
  const [currentScopeId, setCurrentScopeId] = useState<string>(
    ingestion?.scopeId ?? "",
  )
  const [nearMissWarnings, setNearMissWarnings] = useState<NearMissWarning[]>(
    [],
  )
  // Cross-session mapping memory: confirmed PK + column assignments per scope
  const [mappingMemory, setMappingMemory] =
    useState<Record<string, ScopeMappingMemory>>(loadMappingMemory)
  // Loading state for silent detect
  const [, setAnchoring] = useState(false)
  // When user clicks a mode before detect finishes, store intent here
  const [pendingMode, setPendingMode] = useState<
    "import" | "export" | "select" | null
  >(null)
  // Track which flow we're in (import writes to DB, export downloads file)
  const [flowMode, setFlowMode] = useState<"import" | "export">("import")
  // PDF-specific: selected record for implicit extraction
  const [pdfRecordId, setPdfRecordId] = useState<string | null>(
    ingestion?.recordId ?? null,
  )
  // Template mode: which ingestion template the user picked for the PDF flow
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(
    null,
  )
  const started = useRef(false)
  // Generation counter: incremented each time a new ingestion session starts.
  // Async callbacks (buildDiffStateAndEnter) capture this value and bail out
  // if it has changed, preventing stale results from a cancelled wizard from
  // entering diff mode on a subsequent upload.
  const ingestionGen = useRef(0)
  // Tracks which ingestion file the current local state (step, etc.) belongs to.
  // When ingestion changes but the reset effect hasn't run yet, this will be stale
  // and we suppress rendering step content to prevent child components from
  // mounting with leftover state from the previous wizard session.
  const [resetFile, setResetFile] = useState<File | null>(null)

  /** Fire rollup computation for any rollup-flagged mappings, then store results */
  const computeAndStoreRollup = async (
    targetScopeId: string,
    allMappings: SelectMapping[],
    recordIds: string[],
  ) => {
    const rollupMappings = allMappings.filter((m) => m.isRollup && m.visible)
    if (rollupMappings.length === 0) return

    const columns: RollupColumnDef[] = rollupMappings.map((m) => ({
      primitiveId: m.primitiveId,
      primitiveScopeId: m.scopeId,
      primitiveTitle: m.primitiveTitle,
      primitiveType: m.primitiveType,
      label: m.primitiveTitle,
      aggregation: m.primitiveType === "number" ? "sum" : "concat_unique",
    }))

    try {
      const result = await api.computeRollup(targetScopeId, {
        record_ids: recordIds,
        columns: columns.map((c) => ({
          primitive_id: c.primitiveId,
          primitive_scope_id: c.primitiveScopeId,
          aggregation: c.aggregation,
          label: c.label,
        })),
      })

      const values: Record<
        string,
        Record<string, { value: string | null; updated_at?: string | null }>
      > = {}
      for (const v of result.values) {
        if (!values[v.record_id]) values[v.record_id] = {}
        values[v.record_id][v.primitive_id] = {
          value: v.value,
          updated_at: v.updated_at ?? null,
        }
      }

      setRollupState({
        scopeId: targetScopeId,
        columns,
        values,
        includedRecordIds: {},
      })
    } catch (err) {
      console.error("Rollup computation failed:", err)
    }
  }

  // Reset on new ingestion
  useEffect(() => {
    if (!ingestion) {
      started.current = false
      ingestionGen.current++
      setResetFile(null)
      return
    }
    started.current = false
    ingestionGen.current++
    setStep({ id: "parsing" })
    setError(null)
    setAnchorPkPrimId(null)
    setAnchorPkPrimTitle("")
    setAnchorPkPrimType("string")
    setAnchorDetected(null)
    setParsedDoc(null)
    setActiveSheetState(null)
    setCurrentScopeId(ingestion.scopeId)
    setNearMissWarnings([])
    setAnchoring(false)
    setPendingMode(null)
    setFlowMode("import")
    setPdfRecordId(ingestion.recordId ?? null)
    setSelectedTemplateId(null)
    setResetFile(ingestion.file)
  }, [ingestion?.file, ingestion?.scopeId])

  // PDF injection wizard only when opened from a specific record sidebar (recordId present)
  const isPdf = parsedDoc?.file_type === "text" && !!ingestion?.recordId

  // Auto-parse on mount
  useEffect(() => {
    if (!ingestion || step.id !== "parsing" || started.current) return
    started.current = true
    const gen = ingestionGen.current
    ;(async () => {
      try {
        const parsed = await api.ingestUpload(ingestion.scopeId, ingestion.file)
        if (ingestionGen.current !== gen) return
        setParsedDoc(parsed)
        if (parsed.file_type === "excel" && parsed.sheets.length > 1) {
          setStep({ id: "sheet_select", parsed })
        } else {
          const sheet = parsed.sheets[0] ?? null
          setActiveSheetState(sheet)
          // Pre-fill supplier scope for PDF uploads from record sidebar
          if (parsed.file_type === "text" && ingestion.recordId)
            setCurrentScopeId("supplier")
          // Go straight to chooser; anchor+detect deferred until user picks a mode
          setStep({ id: "chooser", parsed, activeSheet: sheet })
        }
      } catch (e) {
        if (ingestionGen.current !== gen) return
        setError(String(e))
      }
    })()
  }, [step.id, ingestion])

  // Auto-transition when detect finishes and user already chose a mode
  useEffect(() => {
    if (
      (pendingMode === "import" || pendingMode === "export") &&
      anchorDetected &&
      step.id === "chooser" &&
      parsedDoc
    ) {
      setFlowMode(pendingMode)
      setPendingMode(null)
      setStep({
        id: "column_mapping",
        parsed: parsedDoc,
        activeSheet: activeSheetState,
        detected: anchorDetected,
        pkPrimitiveId: anchorPkPrimId,
        pkPrimitiveTitle: anchorPkPrimTitle,
        pkPrimitiveType: anchorPkPrimType,
      })
    }
  }, [pendingMode, anchorDetected])

  /** Run detect silently (fuzzy header matching only). Anchor deferred to extraction. */
  async function runSilentDetect(
    parsed: ParsedDocument,
    sheet: ParsedSheet | null,
    scopeId: string,
  ) {
    const gen = ingestionGen.current
    setAnchoring(true)
    setCurrentScopeId(scopeId)
    try {
      const isExcel = parsed.file_type === "excel" && sheet
      const detectPayload = isExcel
        ? {
            file_type: "excel",
            headers: sheet!.headers,
            sample_rows: sheet!.rows.slice(0, 100),
          }
        : { file_type: "text" }

      const detected = await api.ingestDetect(scopeId, detectPayload)

      // Bail if a new ingestion session started while we were awaiting
      if (ingestionGen.current !== gen) return

      // Resolve PK primitive from scope data
      if (ingestion?.recordId) {
        const cached = qc.getQueryData<{
          records: {
            id: string
            values: Record<string, { value: string | null }>
          }[]
          scope: {
            primitives: {
              id: string
              name: string
              type: string
              title: string
              is_pk: boolean
            }[]
          }
        }>(["records", scopeId])
        const pkPrim = cached?.scope.primitives.find((p) => p.is_pk)
        setAnchorPkPrimId(pkPrim?.id ?? null)
        setAnchorPkPrimTitle(pkPrim?.title ?? "")
        setAnchorPkPrimType(pkPrim?.type ?? "string")
      } else {
        // Derive PK info from detect results or scope query
        const scopeData =
          qc.getQueryData<{
            primitives: {
              id: string
              name: string
              type: string
              title: string
              is_pk: boolean
            }[]
          }>(["scope", scopeId]) ??
          (await qc.fetchQuery({
            queryKey: ["scope", scopeId],
            queryFn: () => api.getScope(scopeId),
          }))

        if (ingestionGen.current !== gen) return

        const pkPrim = scopeData?.primitives.find(
          (p: { is_pk: boolean }) => p.is_pk,
        )
        setAnchorPkPrimId(pkPrim?.id ?? null)
        setAnchorPkPrimTitle(pkPrim?.title ?? "")
        setAnchorPkPrimType(pkPrim?.type ?? "string")
      }

      setAnchorDetected(detected)
    } catch (e) {
      if (ingestionGen.current !== gen) return
      setError(translate("QMS/INGESTION/DETECT_FAILED", { error: String(e) }))
    } finally {
      if (ingestionGen.current === gen) setAnchoring(false)
    }
  }

  /** Run anchor + detect-connected for Select Records flow. */
  async function runSelectRecords(
    parsed: ParsedDocument,
    sheet: ParsedSheet | null,
    scopeId: string,
  ) {
    if (!ingestion) return
    const gen = ingestionGen.current
    setPendingMode("select")
    setAnchoring(true)
    try {
      const headers = sheet?.headers ?? []

      if (ingestion.recordId) {
        // Pre-anchored single-record mode: skip anchor, use known PK
        const cached = qc.getQueryData<{
          records: {
            id: string
            values: Record<string, { value: string | null }>
          }[]
          scope: { primitives: { id: string; name: string; is_pk: boolean }[] }
        }>(["records", scopeId])

        const record = cached?.records.find((r) => r.id === ingestion.recordId)
        const pkPrim = cached?.scope.primitives.find((p) => p.is_pk)
        const pkValue =
          pkPrim && record ? (record.values[pkPrim.name]?.value ?? "") : ""

        const detectResult = await api.detectConnected(scopeId, {
          file_type: parsed.file_type,
          headers,
        })

        if (ingestionGen.current !== gen) return

        if (detectResult.matches.length === 0) {
          setError(translate("QMS/INGESTION/NO_COLUMN_MATCH"))
          setPendingMode(null)
          return
        }

        setStep({
          id: "select_mapping",
          parsed,
          activeSheet: sheet,
          pkValues: [pkValue],
          nearMisses: [],
          matches: detectResult.matches,
          anchorMatches: [],
          anchorOut: {
            matches: [],
            new_rows: [],
            near_miss_warnings: [],
            pk_col_idx: null,
            pk_col_header: "",
            pk_col_score: 0,
            pk_primitive_id: pkPrim?.id ?? null,
            pk_primitive_title: "",
            pk_primitive_type: "string",
            mapping_col_idx: null,
            mapping_col_header: "",
            mapping_col_score: 0,
            mapping_primitive_id: null,
            mapping_title: "",
            available_mapping_relations: [],
          },
        })
      } else {
        // Normal flow: run anchor + detect-connected in parallel
        const anchorPayload =
          parsed.file_type === "excel" && sheet
            ? {
                file_type: "excel",
                rows: sheet.rows,
                headers: sheet.headers,
                pk_col_override: null,
              }
            : { file_type: "text", raw_text: parsed.raw_text }

        const [anchorOut, detectResult] = await Promise.all([
          api.ingestAnchor(scopeId, anchorPayload),
          api.detectConnected(scopeId, {
            file_type: parsed.file_type,
            headers,
          }),
        ])

        if (ingestionGen.current !== gen) return

        if (detectResult.matches.length === 0) {
          setError(translate("QMS/INGESTION/NO_COLUMN_MATCH"))
          setPendingMode(null)
          return
        }

        // Extract PK values from exact matches only
        const pkValues = anchorOut.matches.map((m) => m.pk_value)

        if (pkValues.length === 0) {
          setError(translate("QMS/INGESTION/NO_RECORDS_MAPPED"))
          setPendingMode(null)
          return
        }

        setStep({
          id: "select_mapping",
          parsed,
          activeSheet: sheet,
          pkValues,
          nearMisses: anchorOut.near_miss_warnings ?? [],
          matches: detectResult.matches,
          anchorMatches: anchorOut.matches,
          anchorOut,
        })
      }
    } catch (e) {
      if (ingestionGen.current !== gen) return
      setError(
        translate("QMS/INGESTION/SELECT_RECORDS_FAILED", { error: String(e) }),
      )
    } finally {
      if (ingestionGen.current === gen) {
        setAnchoring(false)
        setPendingMode(null)
      }
    }
  }

  /** After extraction: build DiffState and enter diff mode in PivotTable */
  async function buildDiffStateAndEnter(
    extractions: ExtractionEntry[],
    anchored: AnchoredRecord[],
  ) {
    // Capture generation at call time — if it changes during async work,
    // this result belongs to a stale/cancelled wizard session.
    const gen = ingestionGen.current

    // Read current ingestion from store (not stale closure) to guard against
    // async completions after the wizard has been canceled
    const currentIngestion = useStore.getState().ingestion
    if (!currentIngestion) return

    const isExport = flowMode === "export"

    // Enrich with existing values from query cache (fetchQuery fallback ensures
    // data is available even on first run after a page refresh)
    const cached =
      qc.getQueryData<{
        records: {
          id: string
          values: Record<
            string,
            {
              value: string | null
              source: string
              primitive_id: string
              updated_at?: string
            }
          >
        }[]
        scope: {
          primitives: {
            id: string
            name: string
            type: string
            title: string
            is_pk: boolean
            related_scope_id?: string | null
          }[]
        }
      }>(["records", currentScopeId]) ??
      (await qc.fetchQuery({
        queryKey: ["records", currentScopeId],
        queryFn: () => api.listRecords(currentScopeId),
      }))

    // Enrich secondary scope extractions from rollup state
    const rsValues = useStore.getState().rollupState?.values

    const enriched = extractions.map((ex) => {
      // Primary scope: enrich from record cache
      const rec = cached?.records.find((r) => r.id === ex.record_id)
      if (rec && cached) {
        const primDef = cached.scope.primitives.find(
          (p) => p.id === ex.primitive_id,
        )
        if (primDef) {
          const cell = rec.values[primDef.name]
          if (cell?.value) {
            return {
              ...ex,
              existing_value: cell.value,
              existing_source: cell.source,
              existing_updated_at: cell.updated_at,
            }
          }
        }
      }
      // Secondary scope (rollup): enrich from rollup state
      if (rsValues) {
        const rollupEntry = rsValues[ex.record_id]?.[ex.primitive_id]
        if (rollupEntry?.value) {
          return {
            ...ex,
            existing_value: rollupEntry.value,
            existing_source: "rollup",
            existing_updated_at: rollupEntry.updated_at ?? undefined,
          }
        }
      }
      return ex
    })

    // Separate relation extractions from scalar extractions
    const scalarExtractions = enriched.filter(
      (ex) => ex.primitive_type !== "relation",
    )
    const relationExtractions = enriched.filter(
      (ex) => ex.primitive_type === "relation",
    )

    // Build diff records from scalar extractions
    const diffRecords: Record<string, DiffRecord> = {}
    for (const ex of scalarExtractions) {
      // Helper rows carry synthetic primitive ids starting with `_helper_`
      // and type 'formula'.  They follow neither the import-diff (doc vs DB)
      // nor export-diff (DB vs doc) schema — their value is the computed
      // result and has no counterpart in the DB.  We always surface them so
      // the user sees the computed value in the diff view and the download
      // path can flush it into the output file.
      if (
        ex.primitive_type === "formula" &&
        ex.primitive_id.startsWith("_helper_")
      ) {
        if (!diffRecords[ex.record_id]) {
          const anchor = anchored.find((a) => a.record_id === ex.record_id)
          diffRecords[ex.record_id] = {
            recordId: ex.record_id,
            pkValue: anchor?.pk_value ?? ex.record_id,
            nameValue: anchor?.name_value ?? null,
            isNew: anchor?.is_new ?? false,
            cells: {},
          }
        }
        // Diff layout: dbValue = computed formula result (right side, read-
        // only reference); fileValue = document value the user can override
        // (left side).  For helpers targeting an existing doc header, the
        // document already has a cell value — carry it in fileValue so it
        // shows on the left.  rawFileValue snapshots the original document
        // cell so the RED/GREEN rule ("red if doc had content") stays
        // stable across user edits.
        const docCellVal = ex.existing_value ?? null
        diffRecords[ex.record_id].cells[ex.primitive_id] = {
          recordId: ex.record_id,
          primitiveId: ex.primitive_id,
          primitiveName: ex.primitive_title,
          primitiveTitle: ex.primitive_title,
          primitiveType: "formula" as PrimitiveType,
          dbValue: ex.value,
          fileValue: docCellVal,
          rawFileValue: docCellVal,
          typeIncompatible: false,
          accepted: null,
        }
        continue
      }

      // PKs are never editable for existing records — but new records need a PK diff cell
      const _primDef = cached?.scope.primitives.find(
        (p) => p.id === ex.primitive_id,
      )
      const anchorForPk = _primDef?.is_pk
        ? anchored.find((a) => a.record_id === ex.record_id)
        : null
      if (_primDef?.is_pk && !anchorForPk?.is_new) continue

      if (isExport) {
        const dbVal = ex.existing_value ?? null
        const docVal = ex.value
        if (dbVal === null || dbVal === "") continue
        if (docVal != null && dbVal === docVal) continue

        const typeIncompatible = checkTypeIncompatible(
          dbVal,
          ex.primitive_type as PrimitiveType,
        )

        if (!diffRecords[ex.record_id]) {
          const anchor = anchored.find((a) => a.record_id === ex.record_id)
          diffRecords[ex.record_id] = {
            recordId: ex.record_id,
            pkValue: anchor?.pk_value ?? ex.record_id,
            nameValue: anchor?.name_value ?? null,
            isNew: false,
            cells: {},
          }
        }

        const primDef = cached?.scope.primitives.find(
          (p) => p.id === ex.primitive_id,
        )
        // Layout: dbValue (right, timestamp) = DB value, reference only.
        // fileValue (left, accept/edit/decline) = the document cell —
        // what ends up in the exported file.  rawFileValue snapshots the
        // ORIGINAL doc cell so the RED/GREEN rule ("red if doc had
        // content") stays stable even after the user edits fileValue.
        diffRecords[ex.record_id].cells[ex.primitive_id] = {
          recordId: ex.record_id,
          primitiveId: ex.primitive_id,
          primitiveName: primDef?.name ?? ex.primitive_id,
          primitiveTitle: ex.primitive_title,
          primitiveType: ex.primitive_type as PrimitiveType,
          dbValue: dbVal,
          fileValue: docVal,
          rawFileValue: docVal,
          typeIncompatible,
          accepted: null,
          lastHistorySource: ex.existing_source ?? null,
          lastHistoryTimestamp: ex.existing_updated_at ?? null,
        }
      } else {
        if (ex.value === null || ex.value === "") continue
        if (ex.existing_value != null && ex.value === ex.existing_value)
          continue

        const typeIncompatible = checkTypeIncompatible(
          ex.value,
          ex.primitive_type as PrimitiveType,
        )

        if (!diffRecords[ex.record_id]) {
          const anchor = anchored.find((a) => a.record_id === ex.record_id)
          diffRecords[ex.record_id] = {
            recordId: ex.record_id,
            pkValue: anchor?.pk_value ?? ex.record_id,
            nameValue: anchor?.name_value ?? null,
            isNew: anchor?.is_new ?? false,
            cells: {},
          }
        }

        const primDef = cached?.scope.primitives.find(
          (p) => p.id === ex.primitive_id,
        )
        diffRecords[ex.record_id].cells[ex.primitive_id] = {
          recordId: ex.record_id,
          primitiveId: ex.primitive_id,
          primitiveName: primDef?.name ?? ex.primitive_id,
          primitiveTitle: ex.primitive_title,
          primitiveType: ex.primitive_type as PrimitiveType,
          dbValue: ex.existing_value ?? null,
          fileValue: ex.value,
          rawFileValue: ex.raw_value,
          typeIncompatible,
          accepted: null,
          lastHistorySource: ex.existing_source ?? null,
          lastHistoryTimestamp: ex.existing_updated_at ?? null,
        }
      }
    }

    // Build relation diffs: group extractions by (record_id, primitive_id),
    // fetch current links, compute adds/removes
    const relationDiffs: Record<string, Record<string, RelationDiff>> = {}

    if (relationExtractions.length > 0 && !isExport) {
      // Group extracted target IDs by (recordId, primitiveId)
      const grouped = new Map<
        string,
        {
          recordId: string
          primitiveId: string
          targetIds: Set<string>
          entries: ExtractionEntry[]
        }
      >()
      for (const ex of relationExtractions) {
        const key = `${ex.record_id}|${ex.primitive_id}`
        if (!grouped.has(key)) {
          grouped.set(key, {
            recordId: ex.record_id,
            primitiveId: ex.primitive_id,
            targetIds: new Set(),
            entries: [],
          })
        }
        const g = grouped.get(key)!
        if (ex.value) g.targetIds.add(ex.value)
        g.entries.push(ex)
      }

      // Fetch current relation state for each (record, relation primitive) pair
      const fetchPromises: Promise<void>[] = []
      for (const [, g] of grouped) {
        const primDef = cached?.scope.primitives.find(
          (p) => p.id === g.primitiveId,
        )
        if (!primDef) continue
        fetchPromises.push(
          api
            .getRelationPreview(g.recordId, primDef.name)
            .then((preview) => {
              const currentIds = new Set(preview.records.map((r) => r.id))

              const entries: RelationDiffEntry[] = []

              // Additions: in extracted but not currently linked
              for (const targetId of g.targetIds) {
                if (!currentIds.has(targetId)) {
                  // Look up target info from extracted entries
                  const ex = g.entries.find((e) => e.value === targetId)
                  entries.push({
                    targetId,
                    targetPkValue: ex?.mapped_id ?? targetId,
                    targetNameValue: null,
                    mappedId: ex?.mapped_id ?? null,
                    action: "add",
                    accepted: null,
                  })
                }
              }

              // Removals: currently linked but not in extracted set
              for (const linked of preview.records) {
                if (!g.targetIds.has(linked.id)) {
                  entries.push({
                    targetId: linked.id,
                    targetPkValue: linked.pk_value ?? linked.id,
                    targetNameValue: linked.name_value ?? null,
                    mappedId: linked.mapped_id ?? null,
                    action: "remove",
                    accepted: null,
                    source: linked.relation_source ?? null,
                    timestamp: linked.relation_created_at ?? null,
                  })
                }
              }

              if (entries.length > 0) {
                if (!relationDiffs[g.recordId]) relationDiffs[g.recordId] = {}
                relationDiffs[g.recordId][g.primitiveId] = {
                  recordId: g.recordId,
                  primitiveId: g.primitiveId,
                  primitiveName: primDef.name,
                  primitiveTitle: primDef.title,
                  relatedScopeId: primDef.related_scope_id ?? "",
                  currentCount: preview.records.length,
                  entries,
                  cachedRelationData: preview,
                }

                // Ensure this record appears in diffRecords even if it has no scalar diffs
                if (!diffRecords[g.recordId]) {
                  const anchor = anchored.find(
                    (a) => a.record_id === g.recordId,
                  )
                  diffRecords[g.recordId] = {
                    recordId: g.recordId,
                    pkValue: anchor?.pk_value ?? g.recordId,
                    nameValue: anchor?.name_value ?? null,
                    isNew: anchor?.is_new ?? false,
                    cells: {},
                  }
                }
              }
            })
            .catch((err) => {
              console.warn(
                "Relation preview fetch failed:",
                g.recordId,
                primDef.name,
                err,
              )
              // Even if preview fails, treat all extracted targets as additions
              const entries: RelationDiffEntry[] = []
              for (const targetId of g.targetIds) {
                const ex = g.entries.find((e) => e.value === targetId)
                entries.push({
                  targetId,
                  targetPkValue: ex?.mapped_id ?? targetId,
                  targetNameValue: null,
                  mappedId: ex?.mapped_id ?? null,
                  action: "add",
                  accepted: null,
                })
              }
              // Build an empty preview so the expanded row doesn't re-fetch
              const emptyPreview: import("../../types").RelationPreview = {
                primitive_id: g.primitiveId,
                primitive_name: primDef.name,
                related_scope_id: primDef.related_scope_id ?? "",
                records: [],
                scope_primitives: [],
              }
              if (entries.length > 0) {
                if (!relationDiffs[g.recordId]) relationDiffs[g.recordId] = {}
                relationDiffs[g.recordId][g.primitiveId] = {
                  recordId: g.recordId,
                  primitiveId: g.primitiveId,
                  primitiveName: primDef.name,
                  primitiveTitle: primDef.title,
                  relatedScopeId: primDef.related_scope_id ?? "",
                  currentCount: 0,
                  entries,
                  cachedRelationData: emptyPreview,
                }
                if (!diffRecords[g.recordId]) {
                  const anchor = anchored.find(
                    (a) => a.record_id === g.recordId,
                  )
                  diffRecords[g.recordId] = {
                    recordId: g.recordId,
                    pkValue: anchor?.pk_value ?? g.recordId,
                    nameValue: anchor?.name_value ?? null,
                    isNew: anchor?.is_new ?? false,
                    cells: {},
                  }
                }
              }
            }),
        )
      }
      await Promise.all(fetchPromises)
    }

    // Filter out records with no diff cells and no relation diffs
    let filteredRecords: Record<string, DiffRecord> = {}
    for (const [rid, rec] of Object.entries(diffRecords)) {
      if (Object.keys(rec.cells).length > 0 || relationDiffs[rid]) {
        filteredRecords[rid] = rec
      }
    }

    // If a record selection is active, scope import to only selected records
    // (skip this filter in single-record mode — the record is explicitly chosen)
    if (recordSelection && !isExport && !currentIngestion?.recordId) {
      const allowedPks = new Set(
        recordSelection.pkSets
          .flatMap((ps) => ps.pkValues)
          .map((v) => v.trim().toLowerCase()),
      )
      const scoped: Record<string, DiffRecord> = {}
      for (const [rid, rec] of Object.entries(filteredRecords)) {
        if (rec.isNew) {
          scoped[rid] = rec
        } else {
          const anchor = anchored.find((a) => a.record_id === rid)
          if (anchor && allowedPks.has(anchor.pk_value.trim().toLowerCase())) {
            scoped[rid] = rec
          }
        }
      }
      filteredRecords = scoped
    }

    if (Object.keys(filteredRecords).length === 0) {
      setError(translate("QMS/INGESTION/NO_VALUES"))
      if (parsedDoc)
        setStep({
          id: "chooser",
          parsed: parsedDoc,
          activeSheet: activeSheetState,
        })
      return
    }

    // For export, attach the original doc data so PivotTable can reconstruct the file
    const sheet = activeSheetState
    const diffState: DiffState = {
      scopeId: currentScopeId,
      fileName: currentIngestion.file.name,
      records: filteredRecords,
      rawExtractions: enriched,
      anchoredRecords: anchored,
      ...(Object.keys(relationDiffs).length > 0 ? { relationDiffs } : {}),
      ...(isExport
        ? {
            exportMode: true,
            exportParsedHeaders: sheet?.headers ?? [],
            exportParsedRows: sheet?.rows ?? [],
            exportDelimiter: ",",
          }
        : {}),
    }

    // Bail out if a new ingestion session started while we were building the diff
    if (ingestionGen.current !== gen) return

    enterDiffMode(diffState)

    // Pre-populate secondary diffs for related scopes with effectively-stale
    // records. Uses the directional-cascade endpoint so a record counts as
    // stale when it OR any upstream (lower-rank) reachable record is stale.
    if (!isExport) {
      const store = useStore.getState()
      const allScopes = store.scopes
      const currentFreshnessDate = store.freshnessDate
      // When no freshness filter, skip creating secondary diffs
      if (currentFreshnessDate) {
        // Collect per-scope record IDs for a single batched staleness request
        const recordsByScope: Record<string, string[]> = {}
        const scopeSnapshots: Record<string, RecordsPage> = {}
        for (const s of allScopes) {
          if (s.id === currentScopeId) continue
          if (store.actionState?.diffs[s.id]) continue
          const cachedScope = qc.getQueryData<RecordsPage>(["records", s.id])
          if (!cachedScope?.scope || !cachedScope.records?.length) continue
          recordsByScope[s.id] = cachedScope.records.map((r) => r.id)
          scopeSnapshots[s.id] = cachedScope
        }
        let staleSet = new Set<string>()
        if (Object.keys(recordsByScope).length > 0) {
          try {
            const result = await api.getEffectiveStaleness(
              currentFreshnessDate,
              recordsByScope,
            )
            staleSet = new Set(result.stale_record_ids)
          } catch (err) {
            // On failure, fall back to treating every record as stale so the
            // user is forced to review rather than auto-accepting silently.
            console.warn(
              "[IngestionModal] staleness check failed, treating all as stale",
              err,
            )
            staleSet = new Set(Object.values(recordsByScope).flat())
          }
        }
        for (const [sid, cachedScope] of Object.entries(scopeSnapshots)) {
          const pkPrim = cachedScope.scope.primitives.find(
            (p: PrimitiveDef) => p.is_pk,
          )
          const namePrim = cachedScope.scope.primitives.find(
            (p: PrimitiveDef) =>
              !p.is_pk && p.type === "string" && p.is_visible,
          )
          const secRecords: Record<string, DiffRecord> = {}
          for (const rec of cachedScope.records) {
            if (!staleSet.has(rec.id)) continue
            secRecords[rec.id] = {
              recordId: rec.id,
              pkValue: pkPrim
                ? (rec.values[pkPrim.name]?.value ?? rec.id)
                : rec.id,
              nameValue: namePrim
                ? (rec.values[namePrim.name]?.value ?? null)
                : null,
              isNew: false,
              cells: {}, // No per-cell secondary diffs — row-level confirmation only
              rowUpdatedAt: rec.updated_at,
              rowUpdatedSource: rec.updated_source,
              rowFreshnessAccepted: null,
            }
          }
          if (Object.keys(secRecords).length > 0) {
            useStore.getState().setActionScopeDiff(sid, {
              scopeId: sid,
              fileName: translate("QMS/INGESTION/CONTEXT_FILENAME"),
              records: secRecords,
              rawExtractions: [],
              anchoredRecords: [],
              secondaryDiff: true,
            })
          }
        }
      }
    }
  }

  function handleClose() {
    ingestionGen.current++
    closeIngestion()
    setStep({ id: "parsing" })
    setError(null)
  }

  if (!ingestion) return null

  // Guard: when ingestion just changed but the reset effect hasn't fired yet,
  // local state (step, anchorDetected, etc.) is stale from the previous session.
  // Render only a spinner to prevent child components (e.g. ExtractionRunStep)
  // from mounting with leftover state and triggering enterDiffMode.
  const staleSession = ingestion.file !== resetFile
  if (staleSession) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-xs">
        <div className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-grey2 shadow-2xl">
          <div className="flex-1 overflow-y-auto p-6">
            <Spinner label={translate("QMS/INGESTION/READING")} />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-xs">
      <div className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-grey2 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b px-6 py-4">
          <div>
            <h2 className="text-base font-semibold text-white">
              {step.id === "select_mapping"
                ? translate("QMS/INGESTION/SELECT_RECORDS_TITLE")
                : flowMode === "export"
                  ? translate("QMS/INGESTION/EXPORT_TITLE")
                  : translate("QMS/INGESTION/IMPORT_TITLE")}
            </h2>
            <div className="mt-0.5 flex items-center gap-2">
              <p className="text-xs text-grey5">{ingestion.file.name}</p>
              {ingestion.recordId && (
                <>
                  <span className="text-xs text-grey5">·</span>
                  <p className="font-mono text-xs text-grey5">
                    {translate("QMS/INGESTION/SINGLE_RECORD")}
                  </p>
                </>
              )}
            </div>
          </div>
          <StepBreadcrumb stepId={step.id} />
          <button
            onClick={handleClose}
            className="ml-4 p-1 text-xl leading-none text-grey5 hover:text-grey6"
          >
            x
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="mb-4 flex items-start gap-2 rounded-lg border border-red1/30 bg-red1/10 p-3 text-sm text-red1">
              <span className="flex-1">{error}</span>
              <button
                onClick={() => setError(null)}
                className="text-red1 hover:text-red1"
              >
                x
              </button>
            </div>
          )}

          {step.id === "parsing" && (
            <Spinner label={translate("QMS/INGESTION/READING")} />
          )}

          {step.id === "sheet_select" && (
            <SheetSelectStep
              parsed={step.parsed}
              onSelect={(sheet) => {
                setError(null)
                setActiveSheetState(sheet)
                setStep({
                  id: "chooser",
                  parsed: step.parsed,
                  activeSheet: sheet,
                })
              }}
            />
          )}

          {step.id === "chooser" && (
            <ChooserStep
              pendingMode={pendingMode}
              hasExistingSelection={recordSelection !== null}
              currentScopeId={currentScopeId}
              onScopeChange={(newScopeId) => setCurrentScopeId(newScopeId)}
              isSingleRecord={!!ingestion?.recordId}
              isPdf={isPdf}
              pdfRecordId={pdfRecordId}
              onPdfRecordChange={setPdfRecordId}
              onImportChanges={() => {
                if (isPdf) {
                  // PDF: skip detect, go straight to PDF mapping step
                  setFlowMode("import")
                  setStep({
                    id: "column_mapping",
                    parsed: step.parsed,
                    activeSheet: null,
                    detected: {
                      explicit: [],
                      implicit: [],
                      hierarchy_candidates: [],
                    } as DetectOut,
                    pkPrimitiveId: null,
                    pkPrimitiveTitle: "",
                    pkPrimitiveType: "string",
                  })
                } else {
                  setPendingMode("import")
                  runSilentDetect(step.parsed, step.activeSheet, currentScopeId)
                }
              }}
              onExportChanges={() => {
                setPendingMode("export")
                runSilentDetect(step.parsed, step.activeSheet, currentScopeId)
              }}
              onSelectRecords={() => {
                runSelectRecords(step.parsed, step.activeSheet, currentScopeId)
              }}
            />
          )}

          {step.id === "column_mapping" &&
            !isPdf &&
            nearMissWarnings.length > 0 && (
              <NearMissWarningPanel warnings={nearMissWarnings} />
            )}

          {step.id === "column_mapping" && isPdf && (
            <TemplateSelectStep
              anchorScopeId={currentScopeId}
              selectedTemplateId={selectedTemplateId}
              onSelect={setSelectedTemplateId}
              onProceed={() => {
                setStep({
                  id: "extracting",
                  parsed: step.parsed,
                  activeSheet: null,
                  mappings: [],
                  pkPrimitiveId: null,
                })
              }}
            />
          )}

          {step.id === "column_mapping" && !isPdf && (
            <ColumnMappingStep
              scopeId={currentScopeId}
              sheet={step.activeSheet}
              detected={step.detected}
              pkPrimitiveId={step.pkPrimitiveId}
              pkPrimitiveTitle={step.pkPrimitiveTitle}
              pkPrimitiveType={step.pkPrimitiveType}
              pkOptional={!!ingestion?.recordId}
              isExportFlow={flowMode === "export"}
              pkValue={
                ingestion?.recordId
                  ? (() => {
                      const cached = qc.getQueryData<{
                        records: {
                          id: string
                          values: Record<string, { value: string | null }>
                        }[]
                        scope: {
                          primitives: {
                            id: string
                            name: string
                            is_pk: boolean
                          }[]
                        }
                      }>(["records", currentScopeId])
                      const record = cached?.records.find(
                        (r) => r.id === ingestion.recordId,
                      )
                      const pkPrim = cached?.scope.primitives.find(
                        (p) => p.is_pk,
                      )
                      return pkPrim && record
                        ? (record.values[pkPrim.name]?.value ?? "")
                        : ""
                    })()
                  : undefined
              }
              presetMappings={mappingMemory[currentScopeId]?.mappings}
              onProceed={(mappings, anchorChoice) => {
                // Persist confirmed column mappings for all scopes
                const pkRow = mappings.find(
                  (m) =>
                    step.pkPrimitiveId && m.primitive_id === step.pkPrimitiveId,
                )
                const newMemory: Record<string, ScopeMappingMemory> = {
                  ...mappingMemory,
                  [currentScopeId]: {
                    pkColIdx: pkRow?.column_index ?? null,
                    pkPrimitiveId: step.pkPrimitiveId,
                    pkPrimitiveTitle: step.pkPrimitiveTitle,
                    pkPrimitiveType: step.pkPrimitiveType,
                    mappings: mappings.filter(
                      (m) => m.primitive_scope_id === currentScopeId,
                    ),
                  },
                }
                // Save secondary scope mappings separately
                const secondaryScopeIds = [
                  ...new Set(
                    mappings
                      .filter((m) => m.primitive_scope_id !== currentScopeId)
                      .map((m) => m.primitive_scope_id),
                  ),
                ]
                for (const secId of secondaryScopeIds) {
                  const secMappings = mappings.filter(
                    (m) => m.primitive_scope_id === secId,
                  )
                  // Find the relation column that anchors this scope
                  const relMapping = mappings.find(
                    (m) =>
                      m.primitive_type === "relation" &&
                      m.related_scope_id === secId,
                  )
                  newMemory[secId] = {
                    pkColIdx: relMapping?.column_index ?? null,
                    pkPrimitiveId: null, // secondary scopes use relation anchor, not PK
                    pkPrimitiveTitle: "",
                    pkPrimitiveType: "",
                    mappings: secMappings,
                  }
                }
                setMappingMemory(newMemory)
                saveMappingMemory(newMemory)

                setStep({
                  id: "extracting",
                  parsed: step.parsed,
                  activeSheet: step.activeSheet,
                  mappings,
                  pkPrimitiveId: step.pkPrimitiveId,
                  anchorChoice,
                })
              }}
            />
          )}

          {step.id === "extracting" && flowMode === "export" && (
            <ExportExtractionRunStep
              scopeId={currentScopeId}
              activeSheet={step.activeSheet}
              mappings={step.mappings}
              pkPrimitiveId={step.pkPrimitiveId}
              singleRecordId={ingestion?.recordId}
              anchorChoice={step.anchorChoice}
              onDone={(extractions, anchored) => {
                buildDiffStateAndEnter(extractions, anchored)
              }}
              onError={setError}
            />
          )}

          {step.id === "extracting" &&
            isPdf &&
            pdfRecordId &&
            selectedTemplateId && (
              <PdfExtractionStep
                templateId={selectedTemplateId}
                anchorRecordId={pdfRecordId}
                anchorScopeId={currentScopeId}
                fileName={ingestion.file.name}
                onDone={(diffState) => {
                  enterDiffMode(diffState)
                }}
                onError={(msg) => {
                  setError(msg)
                  if (parsedDoc)
                    setStep({
                      id: "chooser",
                      parsed: parsedDoc,
                      activeSheet: activeSheetState,
                    })
                }}
              />
            )}

          {step.id === "select_mapping" && (
            <>
              {step.nearMisses.length > 0 && (
                <NearMissWarningPanel warnings={step.nearMisses} />
              )}
              <SelectMappingStep
                matches={step.matches}
                pkValues={step.pkValues}
                nearMisses={step.nearMisses}
                anchorMatches={step.anchorMatches}
                anchorOut={step.anchorOut}
                headers={step.activeSheet?.headers ?? []}
                scopeId={currentScopeId}
                fileName={ingestion.file.name}
                existingSelection={recordSelection}
                onApplyFresh={(selection) => {
                  setRecordSelection(selection)
                  const recordIds = step.anchorMatches.map((m) => m.record_id)
                  computeAndStoreRollup(
                    currentScopeId,
                    selection.mappings,
                    recordIds,
                  )
                  handleClose()
                }}
                onApplySameScope={(pkValues, fileName, mappings) => {
                  addSameScopePks(currentScopeId, pkValues, fileName, mappings)
                  const recordIds = step.anchorMatches.map((m) => m.record_id)
                  computeAndStoreRollup(currentScopeId, mappings, recordIds)
                  handleClose()
                }}
                onApplyCrossScope={(pkSet, mappings, nearMisses, operator) => {
                  addCrossScopePkSet(pkSet, mappings, nearMisses, operator)
                  const recordIds = step.anchorMatches.map((m) => m.record_id)
                  computeAndStoreRollup(currentScopeId, mappings, recordIds)
                  handleClose()
                }}
                onCancel={handleClose}
              />
            </>
          )}

          {step.id === "extracting" &&
            flowMode !== "export" &&
            !isPdf &&
            ingestion?.recordId && (
              <SingleRecordExtractionStep
                scopeId={currentScopeId}
                activeSheet={step.activeSheet}
                mappings={step.mappings}
                recordId={ingestion.recordId}
                onDone={(extractions, anchored) => {
                  buildDiffStateAndEnter(extractions, anchored)
                }}
                onError={setError}
              />
            )}

          {step.id === "extracting" &&
            flowMode !== "export" &&
            !isPdf &&
            !ingestion?.recordId && (
              <ExtractionRunStep
                scopeId={currentScopeId}
                activeSheet={step.activeSheet}
                mappings={step.mappings}
                pkPrimitiveId={step.pkPrimitiveId}
                anchorChoice={step.anchorChoice}
                onDone={(extractions, anchored) => {
                  buildDiffStateAndEnter(extractions, anchored)
                }}
                onError={setError}
              />
            )}
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Step breadcrumb
// ---------------------------------------------------------------------------

function StepBreadcrumb({ stepId }: { stepId: string }) {
  const { translate } = useTranslation()
  if (stepId === "select_mapping") {
    // Select Records has its own simpler breadcrumb
    return (
      <div className="flex items-center gap-0.5 text-xs">
        <span className="rounded px-2 py-1 text-grey5">
          {translate("QMS/INGESTION/STEP_PARSING")}
        </span>
        <span className="rounded px-2 py-1 text-grey5">
          {translate("QMS/INGESTION/STEP_CHOOSER")}
        </span>
        <span className="rounded bg-yellow5/20 px-2 py-1 font-semibold text-yellow1">
          {translate("QMS/INGESTION/SELECT_FILE")}
        </span>
      </div>
    )
  }
  const cur = STEP_ORDER.indexOf(stepId === "sheet_select" ? "parsing" : stepId)
  return (
    <div className="flex items-center gap-0.5 text-xs">
      {STEP_ORDER.map((s, i) => (
        <span
          key={s}
          className={clsx(
            "rounded px-2 py-1",
            i === cur && "bg-yellow5/20 font-semibold text-yellow1",
            i < cur && "text-grey5",
            i > cur && "text-grey5",
          )}
        >
          {getStepLabels(translate)[s]}
        </span>
      ))}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Spinner
// ---------------------------------------------------------------------------

function Spinner({ label }: { label: string }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-16">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-yellow5 border-t-yellow1" />
      <p className="text-sm text-grey5">{label}</p>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Type compatibility check
// ---------------------------------------------------------------------------

// checkTypeIncompatible and validateColumnType are now imported from ./ColumnMappingStep

// ---------------------------------------------------------------------------
// Chooser Step — three-button mode selection
// ---------------------------------------------------------------------------

function ChooserStep({
  pendingMode,
  hasExistingSelection,
  currentScopeId,
  onScopeChange,
  isSingleRecord,
  isPdf,
  pdfRecordId,
  onPdfRecordChange,
  onImportChanges,
  onExportChanges,
  onSelectRecords,
}: {
  pendingMode: "import" | "export" | "select" | null
  hasExistingSelection: boolean
  currentScopeId: string
  onScopeChange: (scopeId: string) => void
  isSingleRecord: boolean
  isPdf?: boolean
  pdfRecordId?: string | null
  onPdfRecordChange?: (recordId: string | null) => void
  onImportChanges: () => void
  onExportChanges: () => void
  onSelectRecords: () => void
}) {
  const { translate } = useTranslation()
  const { data: scopeList } = useQuery({
    queryKey: ["scopes-list"],
    queryFn: () => api.listScopes(),
  })
  // PDF flow: load records for the selected scope so user can pick a record
  const { data: pdfRecordsPage } = useQuery({
    queryKey: ["records", currentScopeId],
    queryFn: () => api.listRecords(currentScopeId),
    enabled: !!isPdf && !!currentScopeId,
  })
  const actionState = useStore((s) => s.actionState)
  const anchorLocked = actionState?.anchorLocked ?? false
  // Select Records disabled when records are already selected (via clicks or file)
  const selectRecordsDisabled =
    hasExistingSelection && actionState?.type === "select"

  const selectableScopes = (scopeList ?? []).filter(
    (s) => !s.is_hidden && !s.is_singleton,
  )
  const currentScopeName =
    selectableScopes.find((s) => s.id === currentScopeId)?.name ??
    currentScopeId
  const pdfNotReady = !!isPdf && !pdfRecordId

  return (
    <div>
      {/* Scope selector + freshness filter side by side */}
      <div className="mb-6 flex items-start gap-6 rounded-xl border border-grey4 bg-grey1 p-4">
        <div className="min-w-0 flex-1">
          <label className="mb-2 block text-xs font-medium text-grey5">
            {translate("QMS/INGESTION/WORKING_WITH")}
          </label>
          {isSingleRecord ? (
            <div className="text-lg font-semibold capitalize text-white">
              {currentScopeName}
            </div>
          ) : anchorLocked ? (
            <div>
              <div className="text-lg font-semibold capitalize text-white">
                {currentScopeName}
              </div>
              <p className="mt-1 text-[10px] text-grey5">
                {translate("QMS/INGESTION/ANCHOR_LOCKED")}
              </p>
            </div>
          ) : (
            <select
              value={currentScopeId}
              onChange={(e) => onScopeChange(e.target.value)}
              disabled={pendingMode !== null}
              className="w-full rounded-lg border border-grey4 bg-grey2 px-3 py-2.5 text-lg font-semibold capitalize text-white focus:outline-hidden focus:ring-2 focus:ring-yellow1 disabled:opacity-50"
            >
              {selectableScopes.map((s) => (
                <option key={s.id} value={s.id} className="capitalize">
                  {s.name}
                </option>
              ))}
            </select>
          )}
        </div>
        {isPdf && (
          <div className="min-w-0 flex-1">
            <label className="mb-2 block text-xs font-medium text-grey5">
              {translate("QMS/INGESTION/RECORD_LABEL")}:
            </label>
            {isSingleRecord && pdfRecordId ? (
              <div className="text-lg font-semibold text-white">
                {pdfRecordsPage?.records.find((r) => r.id === pdfRecordId)
                  ?.values?.name?.value ??
                  pdfRecordsPage?.scope.primitives
                    .filter((p) => p.is_pk)
                    .map(
                      (pk) =>
                        pdfRecordsPage.records.find((r) => r.id === pdfRecordId)
                          ?.values?.[pk.name]?.value,
                    )
                    .join("") ??
                  pdfRecordId}
              </div>
            ) : (
              <select
                value={pdfRecordId ?? ""}
                onChange={(e) => onPdfRecordChange?.(e.target.value || null)}
                disabled={pendingMode !== null}
                className="w-full rounded-lg border border-grey4 bg-grey2 px-3 py-2.5 text-sm font-medium text-white focus:outline-hidden focus:ring-2 focus:ring-yellow1 disabled:opacity-50"
              >
                <option value="">
                  {translate("QMS/INGESTION/SELECT_OPTION")}
                </option>
                {(pdfRecordsPage?.records ?? []).map((rec) => {
                  const pkPrim = pdfRecordsPage?.scope.primitives.find(
                    (p) => p.is_pk,
                  )
                  const pkVal = pkPrim ? rec.values[pkPrim.name]?.value : rec.id
                  const namePrim = pdfRecordsPage?.scope.primitives.find(
                    (p) => p.name === "name" || p.name === "supplier_id",
                  )
                  const nameVal = namePrim
                    ? rec.values[namePrim.name]?.value
                    : null
                  return (
                    <option key={rec.id} value={rec.id}>
                      {pkVal}
                      {nameVal ? ` — ${nameVal}` : ""}
                    </option>
                  )
                })}
              </select>
            )}
          </div>
        )}
        {!isPdf && (
          <div className="shrink-0">
            <FreshnessDatePicker />
          </div>
        )}
      </div>

      <p className="mb-4 text-sm text-grey5">
        {translate("QMS/INGESTION/SHEET_PROMPT")}
      </p>

      {hasExistingSelection && !selectRecordsDisabled && (
        <div className="mb-4 rounded-lg border border-yellow5 bg-yellow5/10 p-3 text-xs text-yellow1">
          <span className="font-semibold">
            {translate("QMS/INGESTION/ACTIVE_SELECTION_NOTICE")}
          </span>{" "}
          {translate("QMS/INGESTION/IMPORT_SCOPED_DESC")}
        </div>
      )}

      <div className="grid grid-cols-1 gap-3">
        {/* Import Changes */}
        <button
          onClick={onImportChanges}
          disabled={pendingMode === "import" || pdfNotReady}
          className={clsx(
            "flex items-start gap-4 rounded-xl border-2 px-5 py-4 text-left transition-all",
            pdfNotReady
              ? "cursor-not-allowed border-grey4 opacity-50"
              : pendingMode === "import"
                ? "border-yellow1 bg-yellow5/10"
                : "cursor-pointer border-yellow5 hover:border-yellow1 hover:bg-yellow5/10",
          )}
        >
          <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-yellow5/20 text-lg text-yellow1">
            {pendingMode === "import" ? (
              <span className="h-5 w-5 animate-spin rounded-full border-2 border-yellow5 border-t-yellow1" />
            ) : (
              <svg
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                />
              </svg>
            )}
          </div>
          <div>
            <p className="text-sm font-semibold text-white">
              {translate("QMS/INGESTION/IMPORT_CHANGES_TITLE")}
            </p>
            <p className="mt-0.5 text-xs text-grey5">
              {pendingMode === "import"
                ? translate("QMS/INGESTION/ANALYZING_FILE")
                : hasExistingSelection
                  ? translate("QMS/INGESTION/IMPORT_SCOPED_DESC")
                  : translate("QMS/INGESTION/IMPORT_ALL_DESC")}
            </p>
          </div>
        </button>

        {/* Export Changes */}
        <button
          onClick={onExportChanges}
          disabled={pendingMode === "export" || pdfNotReady}
          className={clsx(
            "flex items-start gap-4 rounded-xl border-2 px-5 py-4 text-left transition-all",
            pdfNotReady
              ? "cursor-not-allowed border-grey4 opacity-50"
              : pendingMode === "export"
                ? "border-yellow1 bg-yellow5/10"
                : "cursor-pointer border-yellow5 hover:border-yellow1 hover:bg-yellow5/10",
          )}
        >
          <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-yellow5/20 text-lg text-yellow1">
            {pendingMode === "export" ? (
              <span className="h-5 w-5 animate-spin rounded-full border-2 border-yellow5 border-t-yellow1" />
            ) : (
              <svg
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l4 4m0 0l4-4m-4 4V4"
                />
              </svg>
            )}
          </div>
          <div>
            <p className="text-sm font-semibold text-white">
              {translate("QMS/INGESTION/EXPORT_CHANGES_TITLE")}
            </p>
            <p className="mt-0.5 text-xs text-grey5">
              {pendingMode === "export"
                ? translate("QMS/INGESTION/ANALYZING_FILE")
                : translate("QMS/INGESTION/EXPORT_DESC")}
            </p>
          </div>
        </button>

        {/* Select Records */}
        <button
          onClick={onSelectRecords}
          disabled={
            pendingMode === "select" || !!selectRecordsDisabled || pdfNotReady
          }
          className={clsx(
            "flex items-start gap-4 rounded-xl border-2 px-5 py-4 text-left transition-all",
            selectRecordsDisabled || pdfNotReady
              ? "cursor-not-allowed border-grey4 opacity-50"
              : pendingMode === "select"
                ? "border-yellow1 bg-yellow5/10"
                : "cursor-pointer border-yellow5 hover:border-yellow1 hover:bg-yellow5/10",
          )}
        >
          <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-yellow5/20 text-lg text-yellow1">
            {pendingMode === "select" ? (
              <span className="h-5 w-5 animate-spin rounded-full border-2 border-yellow5 border-t-yellow1" />
            ) : (
              <svg
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
                />
              </svg>
            )}
          </div>
          <div>
            <p
              className={clsx(
                "text-sm font-semibold",
                selectRecordsDisabled ? "text-grey5" : "text-white",
              )}
            >
              {translate("QMS/INGESTION/SELECT_RECORDS_BUTTON")}
            </p>
            <p className="mt-0.5 text-xs text-grey5">
              {selectRecordsDisabled
                ? translate("QMS/INGESTION/DESELECT_FIRST")
                : pendingMode === "select"
                  ? translate("QMS/INGESTION/MAPPING_IN_PROGRESS")
                  : translate("QMS/INGESTION/FOCUS_TABLE")}
            </p>
          </div>
        </button>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Near-Miss Warning Panel — read-only fuzzy PK near-matches
// ---------------------------------------------------------------------------

function NearMissWarningPanel({ warnings }: { warnings: NearMissWarning[] }) {
  const { translate } = useTranslation()
  const [expanded, setExpanded] = useState(false)

  return (
    <div className="mb-4 overflow-hidden rounded-lg border border-warning bg-warning/10">
      <button
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center gap-2 px-4 py-2.5 text-left transition-colors hover:bg-warning/10"
      >
        <svg
          className="h-4 w-4 shrink-0 text-warning"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          />
        </svg>
        <span className="text-xs font-semibold text-warning">
          {translate("QMS/INGESTION/FUZZY_MATCH_COUNT", {
            count: warnings.length,
          })}
        </span>
        <span className="ml-1 text-[10px] text-warning">
          {translate("QMS/INGESTION/FUZZY_MATCH_HEADER")}
        </span>
        <svg
          className={clsx(
            "ml-auto h-3 w-3 text-warning transition-transform",
            expanded && "rotate-180",
          )}
          viewBox="0 0 12 12"
          fill="none"
        >
          <path
            d="M3 5l3 3 3-3"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {expanded && (
        <div className="px-4 pb-3">
          <p className="mb-2 text-[11px] text-warning">
            {translate("QMS/INGESTION/FUZZY_DESC")}
            {translate("QMS/INGESTION/FUZZY_NO_CHANGES")}
          </p>
          <div className="overflow-hidden rounded border border-warning">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-warning bg-warning/20">
                  <th className="px-3 py-1.5 text-left font-medium text-warning">
                    {translate("QMS/INGESTION/DB_PK")}
                  </th>
                  <th className="px-3 py-1.5 text-left font-medium text-warning">
                    {translate("QMS/INGESTION/DOC_PK")}
                  </th>
                  <th className="w-20 px-3 py-1.5 text-right font-medium text-warning">
                    {translate("QMS/INGESTION/SIMILARITY")}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-warning">
                {warnings.map((w) => (
                  <tr key={w.record_id} className="hover:bg-warning/10">
                    <td className="px-3 py-1.5">
                      <span className="font-mono text-warning">{w.db_pk}</span>
                      {w.name_value && (
                        <span className="ml-1.5 text-warning">
                          {w.name_value}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-1.5">
                      <span className="font-mono text-warning">{w.doc_pk}</span>
                    </td>
                    <td className="px-3 py-1.5 text-right">
                      <span
                        className={clsx(
                          "inline-block rounded px-1.5 py-0.5 text-[10px] font-medium",
                          w.score >= 0.9
                            ? "bg-red1/20 text-red1"
                            : w.score >= 0.8
                              ? "bg-warning/20 text-warning"
                              : "bg-grey3 text-grey5",
                        )}
                      >
                        {Math.round(w.score * 100)}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Select Mapping Step — choose which columns/primitives to show
// ---------------------------------------------------------------------------

function SelectMappingStep({
  matches,
  pkValues,
  nearMisses,
  anchorMatches,
  anchorOut,
  headers,
  scopeId,
  fileName,
  existingSelection,
  onApplyFresh,
  onApplySameScope,
  onApplyCrossScope,
  onCancel,
}: {
  matches: DetectConnectedMatch[]
  pkValues: string[]
  nearMisses: NearMissWarning[]
  anchorMatches: AnchorMatch[]
  anchorOut: AnchorOut
  headers: string[]
  scopeId: string
  fileName: string
  existingSelection: RecordSelection | null
  onApplyFresh: (selection: RecordSelection) => void
  onApplySameScope: (
    pkValues: string[],
    fileName: string,
    mappings: SelectMapping[],
  ) => void
  onApplyCrossScope: (
    pkSet: import("../../types").PkSet,
    mappings: SelectMapping[],
    nearMisses: NearMissWarning[],
    operator: "and" | "or",
  ) => void
  onCancel: () => void
}) {
  const { translate } = useTranslation()
  const isSameScope =
    existingSelection?.pkSets.some((ps) => ps.scopeId === scopeId) ?? false
  const isCrossScope = existingSelection !== null && !isSameScope

  // --- Match mode: scope_pk or relation ---
  const hasPkMatches = anchorMatches.some(
    (m) => m.match_type === "pk" || m.match_type === "both",
  )
  const hasMappingMatches = anchorMatches.some(
    (m) => m.match_type === "mapped" || m.match_type === "both",
  )
  const availableRelations = anchorOut.available_mapping_relations ?? []
  const hasRelations = availableRelations.length > 0

  // Default mode: prefer scope_pk if it has matches, else relation if available
  const [matchMode, setMatchMode] = useState<"scope_pk" | "relation">(() => {
    if (hasPkMatches) return "scope_pk"
    if (hasMappingMatches && hasRelations) return "relation"
    return "scope_pk"
  })
  const [selectedRelationId, setSelectedRelationId] = useState<string | null>(
    anchorOut.mapping_primitive_id ??
      (availableRelations.length > 0
        ? availableRelations[0].primitive_id
        : null),
  )

  // Filter matches by mode
  const filteredMatches =
    matchMode === "scope_pk"
      ? anchorMatches.filter(
          (m) => m.match_type === "pk" || m.match_type === "both",
        )
      : anchorMatches.filter(
          (m) => m.match_type === "mapped" || m.match_type === "both",
        )
  const filteredPkValues = filteredMatches.map((m) => m.pk_value)

  const [mappings, setMappings] = useState<SelectMapping[]>(() =>
    matches.map((m) => ({
      primitiveId: m.primitive_id,
      scopeId: m.scope_id,
      scopeName: m.scope_name,
      primitiveTitle: m.primitive_title,
      primitiveType: m.primitive_type,
      header: m.header,
      columnIndex: m.column_index,
      isPk: m.is_pk,
      visible: true,
      originPkSetKey: scopeId,
    })),
  )
  const [crossScopeOp] = useState<"or" | "and">("or")
  const [addDropdownOpen, setAddDropdownOpen] = useState(false)
  const [addSearch, setAddSearch] = useState("")

  // Group mappings by scope for display — current scope first, then others alphabetically
  const byScope = new Map<string, SelectMapping[]>()
  // Ensure current scope appears first
  byScope.set(scopeId, [])
  for (const m of mappings) {
    if (!byScope.has(m.scopeId)) byScope.set(m.scopeId, [])
    byScope.get(m.scopeId)!.push(m)
  }
  // Remove empty current-scope bucket if no mappings for it
  if (byScope.get(scopeId)!.length === 0) byScope.delete(scopeId)

  const selectedRelation = availableRelations.find(
    (r) => r.primitive_id === selectedRelationId,
  )

  // Fetch all scopes so every scope is available as a tab
  const { data: allScopesData, isLoading: allScopesLoading } = useQuery({
    queryKey: ["scopes-list"],
    queryFn: () => api.listScopes(),
  })
  const isHiddenScope = (s: { id: string; is_singleton?: boolean }) =>
    s.is_singleton === true
  const allScopes = (allScopesData ?? []).filter((s) => !isHiddenScope(s))
  const hiddenScopeIds = new Set(
    (allScopesData ?? []).filter((s) => isHiddenScope(s)).map((s) => s.id),
  )

  // Build scope tab list: current scope first, then any scopes that have mappings, then remaining scopes
  const scopeIds = (() => {
    const ids: string[] = []
    // Current scope first (if it has mappings)
    if (byScope.has(scopeId) && !hiddenScopeIds.has(scopeId)) ids.push(scopeId)
    // Scopes with existing mappings
    for (const sid of byScope.keys()) {
      if (!ids.includes(sid) && !hiddenScopeIds.has(sid)) ids.push(sid)
    }
    // All other scopes
    for (const s of allScopes) {
      if (!ids.includes(s.id)) ids.push(s.id)
    }
    return ids
  })()

  const [activeTab, setActiveTab] = useState(scopeId)
  const effectiveTab = scopeIds.includes(activeTab)
    ? activeTab
    : (scopeIds[0] ?? scopeId)
  const tabMappings = byScope.get(effectiveTab) ?? []

  // Fetch full scope details (primitives) for the active tab
  const { data: tabScope } = useQuery({
    queryKey: ["scope", effectiveTab],
    queryFn: () => api.getScope(effectiveTab),
    enabled: !!effectiveTab,
  })

  // Available primitives: all non-relation primitives in the tab scope that aren't already mapped
  const mappedPrimIds = new Set(mappings.map((m) => m.primitiveId))
  const tabAvailablePrims = (tabScope?.primitives ?? []).filter(
    (p) => !mappedPrimIds.has(p.id) && !p.config.hidden,
  )
  const tabFilteredAvailable = addSearch
    ? tabAvailablePrims.filter(
        (p) =>
          p.title.toLowerCase().includes(addSearch.toLowerCase()) ||
          p.name.toLowerCase().includes(addSearch.toLowerCase()),
      )
    : tabAvailablePrims

  // Track which file columns are already mapped (across all scopes) so each header can only be used once
  const usedColumnIndices = new Set(
    mappings.filter((m) => m.columnIndex >= 0).map((m) => m.columnIndex),
  )

  const removePrimitive = (primId: string) => {
    setMappings((prev) => prev.filter((m) => m.primitiveId !== primId))
  }

  const addPrimitive = (p: PrimitiveDef) => {
    const scopeName =
      allScopes.find((s) => s.id === p.scope_id)?.name ?? effectiveTab
    setMappings((prev) => [
      ...prev,
      {
        primitiveId: p.id,
        scopeId: p.scope_id,
        scopeName,
        primitiveTitle: p.title,
        primitiveType: p.type,
        header: "",
        columnIndex: -1,
        isPk: p.is_pk,
        visible: true,
        originPkSetKey: scopeId,
      },
    ])
    setAddDropdownOpen(false)
    setAddSearch("")
  }

  const toggleAll = (visible: boolean) => {
    setMappings((prev) =>
      prev.map((m) => ({ ...m, visible: m.isPk ? true : visible })),
    )
  }

  const toggle = (primitiveId: string) => {
    setMappings((prev) =>
      prev.map((m) =>
        m.primitiveId === primitiveId && !m.isPk
          ? { ...m, visible: !m.visible }
          : m,
      ),
    )
  }

  const updateFileColumn = (primitiveId: string, colIdx: number) => {
    setMappings((prev) =>
      prev.map((m) =>
        m.primitiveId === primitiveId
          ? {
              ...m,
              columnIndex: colIdx,
              header: colIdx >= 0 ? headers[colIdx] : "",
            }
          : m,
      ),
    )
  }

  const toggleRollup = (primitiveId: string) => {
    setMappings((prev) =>
      prev.map((m) =>
        m.primitiveId === primitiveId ? { ...m, isRollup: !m.isRollup } : m,
      ),
    )
  }

  const hasAnyRollup = mappings.some((m) => m.isRollup)

  const visibleCount = mappings.filter((m) => m.visible).length

  const effectivePkValues =
    filteredPkValues.length > 0 ? filteredPkValues : pkValues

  const handleApply = () => {
    if (isSameScope) {
      onApplySameScope(effectivePkValues, fileName, mappings)
    } else if (isCrossScope) {
      onApplyCrossScope(
        { scopeId, pkValues: effectivePkValues, fileNames: [fileName] },
        mappings,
        nearMisses,
        crossScopeOp,
      )
    } else {
      const visibleIds = mappings
        .filter((m) => m.visible)
        .map((m) => m.primitiveId)
      onApplyFresh({
        pkSets: [
          { scopeId, pkValues: effectivePkValues, fileNames: [fileName] },
        ],
        crossScopeOperator: "or",
        nearMisses,
        visiblePrimitiveIds: visibleIds,
        mappings,
      })
    }
  }

  return (
    <div>
      {/* --- Match Mode Selector --- */}
      <div className="mb-4 rounded-lg border border-grey4 bg-grey1 p-3">
        <p className="mb-2 text-xs font-medium text-grey5">
          {translate("QMS/INGESTION/MATCH_BY")}
        </p>
        <div className="flex gap-2">
          <button
            onClick={() => setMatchMode("scope_pk")}
            className={clsx(
              "flex-1 rounded-md border px-3 py-2 text-left text-xs font-medium transition-colors",
              matchMode === "scope_pk"
                ? "border-yellow5 bg-grey2 text-yellow1 shadow-xs ring-1 ring-yellow5"
                : "border-grey4 bg-transparent text-grey5 hover:border-grey4 hover:bg-grey3",
            )}
          >
            <div className="font-semibold">
              {translate("QMS/INGESTION/SCOPE_PK")}
            </div>
            <div className="mt-0.5 text-[10px] text-grey5">
              {anchorOut.pk_col_header ? (
                <>
                  {translate("QMS/ANCHOR/COLUMN_LABEL")}{" "}
                  <span className="font-mono text-grey5">
                    {anchorOut.pk_col_header}
                  </span>{" "}
                  {translate("QMS/INGESTION/SCORE_LABEL", {
                    score: Math.round(anchorOut.pk_col_score),
                  })}
                </>
              ) : (
                translate("QMS/INGESTION/NO_PK_DETECTED")
              )}
            </div>
            {matchMode === "scope_pk" && (
              <div className="mt-1 text-[10px] text-yellow1">
                {translate("QMS/INGESTION/RECORDS_MATCHED", {
                  count: filteredMatches.length,
                })}
              </div>
            )}
          </button>
          <button
            onClick={() => {
              if (!hasRelations) return
              setMatchMode("relation")
            }}
            disabled={!hasRelations}
            className={clsx(
              "flex-1 rounded-md border px-3 py-2 text-left text-xs font-medium transition-colors",
              matchMode === "relation"
                ? "border-yellow5 bg-grey2 text-yellow1 shadow-xs ring-1 ring-yellow5"
                : hasRelations
                  ? "border-grey4 bg-transparent text-grey5 hover:border-grey4 hover:bg-grey3"
                  : "cursor-not-allowed border-grey4/50 bg-transparent text-grey5",
            )}
          >
            <div className="font-semibold">
              {translate("QMS/INGESTION/RELATION_MAPPING")}
            </div>
            <div className="mt-0.5 text-[10px] text-grey5">
              {hasRelations
                ? translate("QMS/INGESTION/RELATIONS_WITH_MAPPING", {
                    count: availableRelations.length,
                  })
                : translate("QMS/INGESTION/NO_RELATIONS")}
            </div>
            {matchMode === "relation" && (
              <div className="mt-1 text-[10px] text-yellow1">
                {translate("QMS/INGESTION/RECORDS_MATCHED", {
                  count: filteredMatches.length,
                })}
              </div>
            )}
          </button>
        </div>

        {/* Relation selector dropdown — only in relation mode */}
        {matchMode === "relation" && hasRelations && (
          <div className="mt-2">
            <label className="text-[10px] font-medium text-grey5">
              {translate("QMS/INGESTION/MAPPING_RELATION_LABEL")}
            </label>
            <select
              value={selectedRelationId ?? ""}
              onChange={(e) => setSelectedRelationId(e.target.value)}
              disabled={availableRelations.length <= 1}
              className="mt-0.5 w-full rounded-md border border-grey4 bg-grey2 px-2.5 py-1.5 text-xs focus:outline-hidden focus:ring-1 focus:ring-yellow1 disabled:opacity-60"
            >
              {availableRelations.map((r) => (
                <option key={r.primitive_id} value={r.primitive_id}>
                  {r.primitive_title} — {r.mapping_title}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* --- Match summary --- */}
      <div className="mb-4">
        <p className="text-sm text-grey5">
          {translate("QMS/INGESTION/MATCHED_SUMMARY", {
            count: effectivePkValues.length,
            mode:
              matchMode === "scope_pk"
                ? translate("QMS/INGESTION/MODE_BY_PK")
                : translate("QMS/INGESTION/MODE_VIA", {
                    relation:
                      selectedRelation?.primitive_title ??
                      translate("QMS/INGESTION/MODE_VIA_FALLBACK"),
                  }),
          })}
          {translate("QMS/INGESTION/CHOOSE_COLUMNS")}
        </p>
      </div>

      {/* --- Scope tabs --- */}
      {allScopesLoading ? (
        <div className="mb-0 flex items-center gap-2 border-b border-grey4 py-3">
          <div className="h-3 w-3 animate-spin rounded-full border-2 border-grey4 border-t-yellow1" />
          <span className="text-xs text-grey5">
            {translate("QMS/INGESTION/SCOPES_LOADING")}
          </span>
        </div>
      ) : (
        <div className="mb-0 flex items-end gap-0.5 overflow-x-auto border-b border-grey4">
          {scopeIds.map((sid) => {
            const scopeMappings = byScope.get(sid) ?? []
            const scopeVisible = scopeMappings.filter((m) => m.visible).length
            const scopeLabel =
              scopeMappings[0]?.scopeName ??
              allScopes.find((s) => s.id === sid)?.name ??
              sid
            const isActive = sid === effectiveTab
            return (
              <button
                key={sid}
                onClick={() => setActiveTab(sid)}
                className={clsx(
                  "flex items-center gap-1.5 whitespace-nowrap border-b-2 px-4 py-2 text-xs font-medium transition-colors",
                  isActive
                    ? "border-yellow1 text-yellow1"
                    : "border-transparent text-grey5 hover:border-grey4 hover:text-white",
                )}
              >
                <span>{scopeLabel}</span>
                {scopeMappings.length > 0 && (
                  <span
                    className={clsx(
                      "text-[10px] tabular-nums",
                      isActive ? "text-yellow1" : "text-grey5",
                    )}
                  >
                    {scopeVisible}/{scopeMappings.length}
                  </span>
                )}
              </button>
            )
          })}
        </div>
      )}

      {/* --- Tab toolbar --- */}
      <div className="flex items-center justify-between py-2">
        <div className="flex gap-2">
          <button
            onClick={() => toggleAll(true)}
            className="text-[10px] text-yellow1 hover:underline"
          >
            {translate("QMS/COLUMN_MAPPING/SHOW_ALL")}
          </button>
          <button
            onClick={() => toggleAll(false)}
            className="text-[10px] text-yellow1 hover:underline"
          >
            {translate("QMS/COLUMN_MAPPING/HIDE_ALL")}
          </button>
        </div>
        <div className="relative">
          <button
            onClick={() => setAddDropdownOpen((v) => !v)}
            className="flex items-center gap-1 rounded-lg border border-yellow5 px-2.5 py-1 text-[11px] font-medium text-yellow1 transition-colors hover:bg-yellow5/10"
          >
            <svg
              className="h-3 w-3"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 4v16m8-8H4"
              />
            </svg>
            {translate("QMS/INGESTION/ADD_COLUMN")}
          </button>
          {addDropdownOpen && (
            <div className="absolute right-0 top-full z-10 mt-1 w-72 overflow-hidden rounded-lg border border-grey4 bg-grey2 shadow-lg">
              <div className="border-b p-2">
                <input
                  type="text"
                  value={addSearch}
                  onChange={(e) => setAddSearch(e.target.value)}
                  placeholder={translate(
                    "QMS/INGESTION/SEARCH_FIELDS_PLACEHOLDER",
                  )}
                  className="w-full rounded-md border border-grey4 px-2.5 py-1.5 text-xs focus:outline-hidden focus:ring-1 focus:ring-yellow1"
                  autoFocus
                />
              </div>
              <div className="max-h-48 overflow-y-auto">
                {tabFilteredAvailable.length === 0 ? (
                  <p className="px-3 py-2 text-xs text-grey5">
                    {tabAvailablePrims.length === 0
                      ? translate("QMS/INGESTION/ALL_COLUMNS_ADDED")
                      : translate("QMS/COMMON/NO_RESULTS")}
                  </p>
                ) : (
                  tabFilteredAvailable.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => addPrimitive(p)}
                      className="flex w-full items-center justify-between px-3 py-2 text-left text-xs transition-colors hover:bg-grey1"
                    >
                      <span>
                        <span className="font-medium text-white">
                          {p.title}
                        </span>
                        {p.is_pk && (
                          <span className="ml-1 rounded bg-yellow5/20 px-1 py-0.5 text-[9px] font-medium text-yellow1">
                            PK
                          </span>
                        )}
                      </span>
                    </button>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* --- Column table for active tab --- */}
      <div className="mb-4 max-h-[35vh] overflow-hidden overflow-y-auto rounded-lg border">
        <table className="w-full text-xs">
          <thead>
            <tr className="sticky top-0 border-b bg-grey1">
              <th className="w-10 px-3 py-1.5 text-left font-medium text-grey5">
                {translate("QMS/INGESTION/COL_SHOW")}
              </th>
              <th className="px-3 py-1.5 text-left font-medium text-grey5">
                {translate("QMS/INGESTION/COL_DB_FIELD")}
              </th>
              <th className="px-3 py-1.5 text-left font-medium text-grey5">
                {translate("QMS/INGESTION/COL_FILE_COLUMN")}
              </th>
              <th
                className="w-14 px-3 py-1.5 text-center font-medium text-grey5"
                title={translate("QMS/INGESTION/AGGREGATION")}
              >
                {translate("QMS/INGESTION/AGGREGATION")}
              </th>
              <th className="w-16 px-3 py-1.5 text-right font-medium text-grey5">
                {translate("QMS/INGESTION/COL_MATCHES")}
              </th>
              <th className="w-8"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-grey4/50">
            {tabMappings.map((m) => {
              const matched = matches.find(
                (x) => x.primitive_id === m.primitiveId,
              )
              return (
                <tr
                  key={m.primitiveId}
                  className={clsx(
                    "transition-colors hover:bg-grey1",
                    !m.visible && "opacity-50",
                  )}
                >
                  <td className="px-3 py-2 text-center">
                    <input
                      type="checkbox"
                      checked={m.visible}
                      disabled={m.isPk}
                      onChange={() => toggle(m.primitiveId)}
                      className="rounded accent-yellow1"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <span className="font-medium text-white">
                      {m.primitiveTitle}
                    </span>
                    {m.isPk && (
                      <span className="ml-1.5 rounded bg-yellow5/20 px-1.5 py-0.5 text-[10px] font-medium text-yellow1">
                        PK
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <select
                      value={m.columnIndex}
                      onChange={(e) =>
                        updateFileColumn(m.primitiveId, Number(e.target.value))
                      }
                      className="font-mono w-full rounded-md border border-grey4 bg-grey2 px-1.5 py-1 text-xs focus:outline-hidden focus:ring-1 focus:ring-yellow1"
                    >
                      <option value={-1}>
                        {translate("QMS/COMMON/NONE_OPTION")}
                      </option>
                      {headers.map((h, idx) => (
                        <option
                          key={idx}
                          value={idx}
                          disabled={
                            usedColumnIndices.has(idx) && idx !== m.columnIndex
                          }
                        >
                          {h}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-3 py-2 text-center">
                    {m.scopeId !== scopeId ? (
                      <input
                        type="checkbox"
                        checked={!!m.isRollup}
                        onChange={() => toggleRollup(m.primitiveId)}
                        className="rounded accent-yellow1"
                        title={
                          m.isRollup
                            ? translate("QMS/INGESTION/ROLLUP_ACTIVE", {
                                type:
                                  m.primitiveType === "number"
                                    ? translate("QMS/INGESTION/ROLLUP_SUM")
                                    : translate("QMS/INGESTION/ROLLUP_UNIQUE"),
                              })
                            : translate("QMS/INGESTION/ROLLUP_ENABLE")
                        }
                      />
                    ) : (
                      <span className="text-[10px] text-grey5">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {matched && (
                      <span className="inline-block rounded bg-success/20 px-1.5 py-0.5 text-[10px] font-medium text-success">
                        {matched.confidence}
                      </span>
                    )}
                  </td>
                  <td className="px-1 py-2 text-center">
                    {!m.isPk && (
                      <button
                        onClick={() => removePrimitive(m.primitiveId)}
                        className="text-grey5 transition-colors hover:text-red1"
                        title={translate("QMS/INGESTION/REMOVE")}
                      >
                        <svg
                          className="h-3.5 w-3.5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      <div className="flex justify-end gap-3">
        <button
          onClick={onCancel}
          className="px-4 py-2 text-sm text-grey5 transition-colors hover:text-white"
        >
          {translate("QMS/COMMON/CANCEL")}
        </button>
        <button
          onClick={handleApply}
          disabled={visibleCount === 0 || effectivePkValues.length === 0}
          className="rounded-lg bg-yellow1 px-4 py-2 text-sm font-medium text-black transition-colors hover:bg-yellow5/30 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {translate("QMS/INGESTION/APPLY_SELECTION", {
            records: effectivePkValues.length,
            columns: visibleCount,
            agg: hasAnyRollup
              ? `, ${mappings.filter((m) => m.isRollup).length} aggregation`
              : "",
          })}
        </button>
      </div>
    </div>
  )
}

// ColumnMappingStep + DiffPreview are now in ./ColumnMappingStep.tsx

// ---------------------------------------------------------------------------
// Scope pills shared helper
// ---------------------------------------------------------------------------

function ScopePills({
  scopeIds,
  active,
  onChange,
  labelSuffix,
}: {
  scopeIds: string[]
  active: string
  onChange: (id: string) => void
  labelSuffix?: (id: string) => string
}) {
  if (scopeIds.length === 0) return null
  return (
    <div className="mb-4 flex flex-wrap gap-1.5">
      {scopeIds.map((id) => (
        <button
          key={id}
          onClick={() => onChange(id)}
          className={clsx(
            "rounded-full px-3 py-1 text-xs font-medium capitalize transition-colors",
            active === id
              ? "bg-yellow1 text-black"
              : "bg-grey3 text-grey5 hover:bg-grey4",
          )}
        >
          {id}
          {labelSuffix ? labelSuffix(id) : ""}
        </button>
      ))}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Sheet Select Step
// ---------------------------------------------------------------------------

function SheetSelectStep({
  parsed,
  onSelect,
}: {
  parsed: ParsedDocument
  onSelect: (s: ParsedSheet) => void
}) {
  const { translate } = useTranslation()
  return (
    <div>
      <p className="mb-4 text-sm text-grey5">
        {translate("QMS/INGESTION/SHEET_PROMPT")}
      </p>
      <div className="flex flex-col gap-2">
        {parsed.sheets.map((sheet, i) => (
          <button
            key={i}
            onClick={() => onSelect(sheet)}
            className="flex items-center gap-3 rounded-lg border px-4 py-3 text-left transition-colors hover:border-yellow5 hover:bg-yellow5/10"
          >
            <span className="text-base">
              {translate("QMS/INGESTION/SHEET_ICON")}
            </span>
            <div>
              <p className="text-sm font-medium text-white">{sheet.name}</p>
              <p className="text-xs text-grey5">
                {translate("QMS/INGESTION/SHEET_STATS", {
                  cols: sheet.headers.length,
                  rows: sheet.rows.length,
                })}
              </p>
            </div>
          </button>
        ))}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Anchor Step
// ---------------------------------------------------------------------------

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function AnchorStep({
  matches,
  newRows,
  pkColIdx,
  headers,
  detected,
  scopeId,
  processedScopeIds,
  onChangePkCol,
  onProceed,
  onFilter,
}: {
  matches: AnchorMatch[]
  newRows: NewRowCandidate[]
  pkColIdx: number | null
  headers: string[]
  processedScopeIds: string[]
  detected: DetectOut | null
  scopeId: string
  onChangePkCol: (colIdx: number) => void
  onProceed: () => void
  onFilter: () => void
}) {
  const { translate } = useTranslation()
  const [showOtherCols, setShowOtherCols] = useState(false)

  // Other-scope columns from detect results (primitives belonging to other scopes)
  const otherScopeExplicit = detected
    ? detected.explicit.filter((e) => e.primitive_scope_id !== scopeId)
    : []
  // Only show other scopes where actual primitive columns were detected (not just relations)
  const otherScopeIds = [
    ...new Set(otherScopeExplicit.map((e) => e.primitive_scope_id)),
  ].filter((id) => !processedScopeIds.includes(id))
  const otherScopeColCount: Record<string, number> = {}
  for (const p of otherScopeExplicit) {
    if (!processedScopeIds.includes(p.primitive_scope_id))
      otherScopeColCount[p.primitive_scope_id] =
        (otherScopeColCount[p.primitive_scope_id] ?? 0) + 1
  }

  // 3 example PK values from the current column selection
  const exampleValues = [
    ...newRows,
    ...matches.map((m) => ({ pk_value: m.pk_value })),
  ]
    .map((r) => r.pk_value)
    .filter((v) => v.trim())
    .slice(0, 3)

  // Detect PK duplicates: new rows whose pk_value matches another new row or an existing match
  const norm = (v: string) => v.trim().toLowerCase()
  const existingPkSet = new Set(matches.map((m) => norm(m.pk_value)))
  const newRowPkCounts = new Map<string, number>()
  for (const nr of newRows) {
    const key = norm(nr.pk_value)
    newRowPkCounts.set(key, (newRowPkCounts.get(key) ?? 0) + 1)
  }
  // Rows absorbed into existing records + excess rows from within new_rows
  const duplicateAbsorbedCount = [...newRowPkCounts.entries()]
    .filter(([k]) => existingPkSet.has(k))
    .reduce((s, [, c]) => s + c, 0)
  const duplicateExcessCount = [...newRowPkCounts.values()].reduce(
    (s, c) => s + Math.max(0, c - 1),
    0,
  )
  const totalDuplicateRows = duplicateAbsorbedCount + duplicateExcessCount
  // Genuine new records = unique pk_values not matching any existing record
  const genuineNewCount = [...newRowPkCounts.keys()].filter(
    (k) => !existingPkSet.has(k),
  ).length

  const total = matches.length + newRows.length

  return (
    <div className="flex flex-col gap-5">
      {/* PK column selector */}
      <div>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-grey5">
          {translate("QMS/INGESTION/PK_COLUMN")}
        </p>
        <p className="mb-3 text-xs text-grey5">
          {translate("QMS/INGESTION/PK_HELP")}
        </p>
        {headers.length > 0 ? (
          <div className="flex items-center gap-3">
            <select
              value={pkColIdx ?? ""}
              onChange={(e) => onChangePkCol(Number(e.target.value))}
              className="rounded-lg border border-grey4 bg-grey2 px-3 py-2 text-sm text-white focus:border-yellow1 focus:outline-hidden"
            >
              <option value="" disabled>
                {translate("QMS/INGESTION/SELECT_OPTION")}
              </option>
              {headers.map((h, i) => (
                <option key={i} value={i}>
                  {h ||
                    translate("QMS/COLUMN_MAPPING/COLUMN_FALLBACK", {
                      index: i + 1,
                    })}
                </option>
              ))}
            </select>
            {exampleValues.length > 0 && (
              <div className="flex min-w-0 items-center gap-1.5">
                <span className="shrink-0 text-[10px] text-grey5">
                  {translate("QMS/INGESTION/EG_ABBR")}
                </span>
                {exampleValues.map((v, i) => (
                  <span
                    key={i}
                    className="font-mono max-w-[100px] truncate rounded bg-grey3 px-1.5 py-0.5 text-xs text-grey5"
                  >
                    {v}
                  </span>
                ))}
              </div>
            )}
          </div>
        ) : (
          <p className="text-xs italic text-grey5">
            {translate("QMS/INGESTION/NO_HEADERS")}
          </p>
        )}
      </div>

      {/* Create / Update summary */}
      <div>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-grey5">
          {translate("QMS/INGESTION/SUMMARY_HEADING")}
        </p>
        {total === 0 ? (
          <p className="text-sm text-grey5">
            {translate("QMS/INGESTION/NO_ROWS")}
          </p>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-lg border border-success/30 bg-success/10 px-4 py-3 text-center">
              <p className="text-2xl font-bold text-success">
                {genuineNewCount}
              </p>
              <p className="mt-0.5 text-xs text-success">
                {translate("QMS/INGESTION/WILL_CREATE")}
              </p>
            </div>
            <div className="rounded-lg border border-yellow5 bg-yellow5/10 px-4 py-3 text-center">
              <p className="text-2xl font-bold text-yellow1">
                {matches.length}
              </p>
              <p className="mt-0.5 text-xs text-yellow1">
                {translate("QMS/INGESTION/WILL_UPDATE")}
              </p>
            </div>
          </div>
        )}
        {totalDuplicateRows > 0 && (
          <div className="mt-3 rounded-lg border border-warning bg-warning/10 px-4 py-3">
            <p className="text-xs font-semibold text-warning">
              {translate("QMS/INGESTION/DUPLICATE_PK_ROWS", {
                count: totalDuplicateRows,
              })}
            </p>
            <p className="mt-1 text-xs text-warning">
              {translate("QMS/INGESTION/DUPLICATE_PKS")}
              {translate("QMS/INGESTION/CONFLICT_HINT")}
            </p>
          </div>
        )}
        <p className="mt-2 text-xs text-grey5">
          {translate("QMS/INGESTION/DESELECT_IN_REVIEW")}
        </p>
      </div>

      {/* Other scopes notice with inline column toggle */}
      {otherScopeIds.length > 0 && (
        <div className="rounded-lg border border-warning bg-warning/10 px-4 py-3">
          <div className="mb-1.5 flex items-start justify-between gap-2">
            <p className="text-xs font-semibold text-warning">
              {translate("QMS/INGESTION/OTHER_SCOPES_DETECTED")}
            </p>
            <button
              onClick={() => setShowOtherCols((s) => !s)}
              className="shrink-0 text-[10px] text-warning underline hover:text-warning"
            >
              {showOtherCols
                ? translate("QMS/INGESTION/HIDE_COLUMNS")
                : translate("QMS/INGESTION/SHOW_COLUMNS")}
            </button>
          </div>
          <div className="mb-2 flex flex-wrap gap-1.5">
            {otherScopeIds.map((id) => (
              <span
                key={id}
                className="rounded-full bg-warning/20 px-2 py-0.5 text-xs font-medium capitalize text-warning"
              >
                {id}
                {otherScopeColCount[id]
                  ? ` (${translate("QMS/INGESTION/COL_COUNT", { count: otherScopeColCount[id] })})`
                  : ""}
              </span>
            ))}
          </div>
          {showOtherCols && (
            <div className="mb-2 divide-y divide-warning overflow-hidden rounded border border-warning">
              {otherScopeIds.map((sid) => {
                const directCols = otherScopeExplicit.filter(
                  (e) => e.primitive_scope_id === sid,
                )
                return (
                  <div key={sid} className="bg-grey2/60 px-3 py-2">
                    <p className="mb-1 text-[10px] font-semibold uppercase capitalize tracking-wide text-grey5">
                      {sid}
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {directCols.map((e) => (
                        <span
                          key={e.primitive_id}
                          className="font-mono rounded bg-warning/20 px-1.5 py-0.5 text-xs text-warning"
                        >
                          {e.matched_header}
                        </span>
                      ))}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
          <p className="text-xs text-warning">
            {translate("QMS/INGESTION/OTHER_SCOPES")}
          </p>
        </div>
      )}

      <div className="flex justify-end gap-2">
        <button
          onClick={onFilter}
          disabled={total === 0 && matches.length === 0}
          className="rounded-lg border border-yellow5 bg-grey2 px-4 py-2 text-sm font-medium text-yellow1 transition-colors hover:bg-yellow5/10 disabled:opacity-40"
        >
          {translate("QMS/INGESTION/FILTER_BTN")}
        </button>
        <button
          onClick={onProceed}
          disabled={total === 0 && matches.length === 0}
          className="rounded-lg bg-yellow1 px-4 py-2 text-sm font-medium text-black transition-colors hover:bg-yellow5/30 disabled:opacity-40"
        >
          {translate("QMS/INGESTION/PROCEED_BTN")}
        </button>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Strategy Step — single-scope column mapping + other-scope info panel
// ---------------------------------------------------------------------------

type StrategyRow = {
  primitive_id: string
  primitive_scope_id: string
  primitive_title: string
  primitive_type: string
  assignment: "explicit" | "implicit"
  column_index: number
  instruction: string
  related_scope_id?: string | null
  confirmed?: boolean // true when column was pre-filled from a prior user-confirmed cascade
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function StrategyStep({
  scopeId,
  sheet,
  detected,
  pkColIdx,
  pkPrimitiveId,
  pkPrimitiveTitle,
  pkPrimitiveType,
  presetMappings,
  pkColByScope,
  onProceed,
}: {
  scopeId: string
  sheet: ParsedSheet | null
  detected: DetectOut
  pkColIdx: number | null
  pkPrimitiveId: string | null
  pkPrimitiveTitle: string
  pkPrimitiveType: string
  presetMappings?: UserMapping[]
  // Map of scopeId → column index where that scope's PK values appear in the document.
  // Derived from all confirmed mapping sessions so far.
  pkColByScope?: Record<string, number>
  onProceed: (
    mappings: UserMapping[],
    implicitCandidates: ImplicitPrimitiveCandidate[],
  ) => void
}) {
  const { translate } = useTranslation()
  const headers = sheet?.headers ?? []
  const previewRows = (sheet?.rows ?? []).slice(0, 3)

  // Current-scope explicit matches, always including the PK row (exclude formula — computed, not imported)
  const [rows, setRows] = useState<StrategyRow[]>(() => {
    const explicitRows = detected.explicit
      .filter(
        (e) =>
          e.primitive_scope_id === scopeId && e.primitive_type !== "formula",
      )
      .map((e) => ({
        primitive_id: e.primitive_id,
        primitive_scope_id: e.primitive_scope_id,
        primitive_title: e.primitive_title,
        primitive_type: e.primitive_type,
        assignment: "explicit" as const,
        column_index: e.column_index,
        instruction: "",
        related_scope_id: e.related_scope_id ?? null,
      }))

    // Ensure the PK primitive is always present, even if detect didn't match it explicitly
    const hasPk =
      pkPrimitiveId != null &&
      explicitRows.some((r) => r.primitive_id === pkPrimitiveId)
    if (!hasPk && pkPrimitiveId != null && pkColIdx != null) {
      explicitRows.unshift({
        primitive_id: pkPrimitiveId,
        primitive_scope_id: scopeId,
        primitive_title: pkPrimitiveTitle,
        primitive_type: pkPrimitiveType,
        assignment: "explicit" as const,
        column_index: pkColIdx,
        instruction: "",
        related_scope_id: null,
      })
    }

    // Auto-promote implicit relation primitives whose target scope column is already
    // known from a prior confirmed mapping session (pkColByScope).
    if (pkColByScope) {
      const alreadyMapped = new Set(explicitRows.map((r) => r.primitive_id))
      for (const cand of detected.implicit) {
        if (
          cand.primitive_scope_id === scopeId &&
          cand.primitive_type === "relation" &&
          cand.related_scope_id &&
          !alreadyMapped.has(cand.primitive_id) &&
          pkColByScope[cand.related_scope_id] != null
        ) {
          explicitRows.push({
            primitive_id: cand.primitive_id,
            primitive_scope_id: cand.primitive_scope_id,
            primitive_title: cand.primitive_title,
            primitive_type: cand.primitive_type,
            assignment: "explicit" as const,
            column_index: pkColByScope[cand.related_scope_id],
            instruction: "",
            related_scope_id: cand.related_scope_id,
          })
          alreadyMapped.add(cand.primitive_id)
        }
      }
    }

    // Apply preset column assignments from a previous confirmed session for this scope,
    // and cross-scope relation column hints from pkColByScope.
    return explicitRows.map((r) => {
      if (pkPrimitiveId != null && r.primitive_id === pkPrimitiveId) return r // PK is fixed by anchor
      // Prefer same-scope preset (most specific)
      if (presetMappings && presetMappings.length > 0) {
        const preset = presetMappings.find(
          (p) => p.primitive_id === r.primitive_id,
        )
        if (preset)
          return { ...r, column_index: preset.column_index, confirmed: true }
      }
      // Cross-scope hint: for relation rows, use the confirmed column for the target scope
      if (
        r.primitive_type === "relation" &&
        r.related_scope_id &&
        pkColByScope?.[r.related_scope_id] != null
      ) {
        return {
          ...r,
          column_index: pkColByScope[r.related_scope_id],
          confirmed: true,
        }
      }
      return r
    })
  })

  // Pool: current-scope implicit candidates not already in rows
  const implicitPool = detected.implicit.filter(
    (c) =>
      c.primitive_scope_id === scopeId &&
      c.primitive_type !== "formula" &&
      !rows.some((r) => r.primitive_id === c.primitive_id),
  )
  const [addSel, setAddSel] = useState("")

  function updateRow(primitiveId: string, patch: Partial<StrategyRow>) {
    setRows((prev) =>
      prev.map((r) =>
        r.primitive_id === primitiveId ? { ...r, ...patch } : r,
      ),
    )
  }

  function removeRow(primitiveId: string) {
    if (pkPrimitiveId != null && primitiveId === pkPrimitiveId) return // PK row is locked
    setRows((prev) => prev.filter((r) => r.primitive_id !== primitiveId))
  }

  function addPrimitive() {
    const cand = detected.implicit.find(
      (c) => c.primitive_id === addSel && c.primitive_scope_id === scopeId,
    )
    if (!cand) return
    const origExplicit = detected.explicit.find(
      (e) => e.primitive_id === cand.primitive_id,
    )
    // Relation primitives are always explicit — they need a column to resolve against
    const isRelation = cand.primitive_type === "relation"
    // Pre-fill with best fuzzy match from detect (even if below threshold)
    const hasBestMatch =
      origExplicit != null || cand.best_match_column_index != null
    setRows((prev) => [
      ...prev,
      {
        primitive_id: cand.primitive_id,
        primitive_scope_id: cand.primitive_scope_id,
        primitive_title: cand.primitive_title,
        primitive_type: cand.primitive_type,
        assignment: isRelation || hasBestMatch ? "explicit" : "implicit",
        column_index:
          origExplicit?.column_index ?? cand.best_match_column_index ?? 0,
        instruction: "",
        related_scope_id: cand.related_scope_id ?? null,
      },
    ])
    setAddSel("")
  }

  const explicitRows = rows.filter((r) => r.assignment === "explicit")

  function proceed() {
    const mappings: UserMapping[] = []
    const implicitCandidates: ImplicitPrimitiveCandidate[] = []
    for (const r of rows) {
      if (r.assignment === "explicit") {
        mappings.push({
          primitive_id: r.primitive_id,
          primitive_scope_id: r.primitive_scope_id,
          primitive_title: r.primitive_title,
          primitive_type: r.primitive_type,
          column_index: r.column_index,
          related_scope_id: r.related_scope_id ?? null,
        })
      } else {
        implicitCandidates.push({
          primitive_id: r.primitive_id,
          primitive_scope_id: r.primitive_scope_id,
          primitive_title: r.primitive_title,
          primitive_type: r.primitive_type,
          instruction: r.instruction || undefined,
        })
      }
    }
    onProceed(mappings, implicitCandidates)
  }

  return (
    <div>
      <p className="mb-4 text-sm text-grey5">
        {translate("QMS/INGESTION/MAP_COLUMNS_DESC_PREFIX")}{" "}
        <span className="font-medium capitalize">{scopeId}</span>
        {translate("QMS/INGESTION/MAP_COLUMNS_DESC_SUFFIX")}
      </p>

      {/* Assignment table */}
      <div className="mb-3 overflow-hidden rounded-lg border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-grey1">
              <th className="w-[32%] px-3 py-2 text-left font-medium text-grey5">
                {translate("QMS/INGESTION/FIELD_HEADER")}
              </th>
              <th className="px-3 py-2 text-left font-medium text-grey5">
                {translate("QMS/INGESTION/COLUMN_MAPPING_HEADER")}
              </th>
              <th className="w-8 px-2 py-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-grey4/50">
            {rows.length === 0 ? (
              <tr>
                <td colSpan={3} className="py-6 text-center text-xs text-grey5">
                  {translate("QMS/INGESTION/NO_COLUMNS_MAPPED")}
                </td>
              </tr>
            ) : (
              rows.map((r) => {
                const origMatch = detected.explicit.find(
                  (e) => e.primitive_id === r.primitive_id,
                )
                const implMatch = detected.implicit.find(
                  (e) => e.primitive_id === r.primitive_id,
                )
                // Pre-filled from a prior confirmed cascade → always high
                const confidence: string | null = r.confirmed
                  ? "high"
                  : (origMatch?.confidence ??
                    implMatch?.best_match_confidence ??
                    null)
                const isPk =
                  pkPrimitiveId != null && r.primitive_id === pkPrimitiveId
                return (
                  <tr
                    key={r.primitive_id}
                    className={isPk ? "bg-grey1/60" : undefined}
                  >
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-1.5">
                        <span className="font-mono text-[10px] text-grey5">
                          [{r.primitive_type}]
                        </span>
                        <span className="font-medium text-white">
                          {r.primitive_title}
                        </span>
                        {isPk ? (
                          <span className="rounded bg-yellow1/15 px-1 py-0.5 text-[10px] font-medium text-yellow1">
                            PK
                          </span>
                        ) : (
                          confidence && (
                            <span
                              className={clsx(
                                "rounded px-1 py-0.5 text-[10px] font-medium",
                                confidence === "high"
                                  ? "bg-success/20 text-success"
                                  : confidence === "medium"
                                    ? "bg-warning/20 text-warning"
                                    : "bg-red1/20 text-red1",
                              )}
                            >
                              {confidence}
                            </span>
                          )
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      {isPk ? (
                        <div className="flex items-center gap-2">
                          <span className="cursor-not-allowed rounded bg-yellow5/20 px-2 py-0.5 text-xs font-medium text-yellow1 opacity-50">
                            {translate("QMS/INGESTION/EXPLICIT_BADGE")}
                          </span>
                          <span className="font-mono text-xs text-grey5">
                            {headers[r.column_index] || `Col ${r.column_index}`}
                          </span>
                          <span className="text-[10px] italic text-grey5">
                            {translate("QMS/INGESTION/SET_IN_ANCHOR_STEP")}
                          </span>
                        </div>
                      ) : r.primitive_type === "relation" ? (
                        // Relation rows are always explicit — AI can't resolve record IDs
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="rounded bg-purple1/20 px-2 py-0.5 text-xs font-medium text-purple1">
                            {translate("QMS/INGESTION/RELATION_BADGE")}
                          </span>
                          {headers.length > 0 && (
                            <select
                              value={r.column_index}
                              onChange={(e) =>
                                updateRow(r.primitive_id, {
                                  column_index: Number(e.target.value),
                                })
                              }
                              className="rounded border bg-grey2 px-1.5 py-0.5 text-xs text-grey6"
                            >
                              {headers.map((h, i) => (
                                <option key={i} value={i}>
                                  {h ||
                                    translate(
                                      "QMS/COLUMN_MAPPING/COLUMN_FALLBACK",
                                      { index: i + 1 },
                                    )}
                                </option>
                              ))}
                            </select>
                          )}
                          <span className="text-[10px] italic text-grey5">
                            {translate("QMS/INGESTION/LINKED_WITH", {
                              scope: r.primitive_scope_id,
                            })}
                          </span>
                        </div>
                      ) : r.assignment === "explicit" ? (
                        <div className="flex flex-wrap items-center gap-2">
                          <button
                            onClick={() =>
                              updateRow(r.primitive_id, {
                                assignment: "implicit",
                              })
                            }
                            className="rounded bg-yellow5/20 px-2 py-0.5 text-xs font-medium text-yellow1 transition-colors hover:bg-yellow5/30"
                            title={translate(
                              "QMS/INGESTION/SWITCH_TO_AI_TITLE",
                            )}
                          >
                            {translate("QMS/INGESTION/EXPLICIT_SWITCH_BTN")}
                          </button>
                          {headers.length > 0 && (
                            <select
                              value={r.column_index}
                              onChange={(e) =>
                                updateRow(r.primitive_id, {
                                  column_index: Number(e.target.value),
                                })
                              }
                              className="rounded border bg-grey2 px-1.5 py-0.5 text-xs text-grey6"
                            >
                              {headers.map((h, i) => (
                                <option key={i} value={i}>
                                  {h ||
                                    translate(
                                      "QMS/COLUMN_MAPPING/COLUMN_FALLBACK",
                                      { index: i + 1 },
                                    )}
                                </option>
                              ))}
                            </select>
                          )}
                        </div>
                      ) : (
                        <div className="flex flex-wrap items-center gap-2">
                          <button
                            onClick={() =>
                              updateRow(r.primitive_id, {
                                assignment: "explicit",
                                column_index: 0,
                              })
                            }
                            className="rounded bg-purple1/20 px-2 py-0.5 text-xs font-medium text-purple1 transition-colors hover:bg-purple1/30"
                            title={translate(
                              "QMS/INGESTION/SWITCH_TO_EXPLICIT_TITLE",
                            )}
                          >
                            AI ⇄
                          </button>
                          <input
                            type="text"
                            value={r.instruction}
                            onChange={(e) =>
                              updateRow(r.primitive_id, {
                                instruction: e.target.value,
                              })
                            }
                            placeholder={translate(
                              "QMS/INGESTION/AI_SEARCH_PLACEHOLDER",
                            )}
                            className="min-w-[140px] flex-1 rounded border bg-grey2 px-2 py-0.5 text-xs text-grey6"
                          />
                        </div>
                      )}
                    </td>
                    <td className="px-2 py-2 text-center">
                      {!isPk && (
                        <button
                          onClick={() => removeRow(r.primitive_id)}
                          title={translate("QMS/INGESTION/REMOVE")}
                          className="text-sm leading-none text-grey5 transition-colors hover:text-red1"
                        >
                          ×
                        </button>
                      )}
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Add field from implicit pool */}
      {implicitPool.length > 0 && (
        <div className="mb-4 flex items-center gap-2">
          <select
            value={addSel}
            onChange={(e) => setAddSel(e.target.value)}
            className="flex-1 rounded border bg-grey2 px-2 py-1.5 text-xs text-grey6"
          >
            <option value="">{translate("QMS/INGESTION/ADD_FIELD")}</option>
            {implicitPool.map((c) => (
              <option key={c.primitive_id} value={c.primitive_id}>
                {c.primitive_title} [{c.primitive_type}]
              </option>
            ))}
          </select>
          <button
            onClick={addPrimitive}
            disabled={!addSel}
            className="rounded border bg-grey3 px-3 py-1.5 text-xs text-grey6 transition-colors hover:bg-grey4 disabled:opacity-40"
          >
            {translate("QMS/INGESTION/ADD")}
          </button>
        </div>
      )}

      {/* Preview */}
      {explicitRows.length > 0 && previewRows.length > 0 && (
        <div className="mb-4">
          <p className="mb-1.5 text-xs text-grey5">
            {translate("QMS/INGESTION/PREVIEW_ROWS", {
              count: previewRows.length,
            })}
          </p>
          <div className="overflow-x-auto rounded border text-xs">
            <table className="min-w-full">
              <thead>
                <tr className="bg-grey1">
                  {explicitRows.map((r) => (
                    <th
                      key={r.primitive_id}
                      className="whitespace-nowrap border-r px-2 py-1.5 text-left font-medium text-grey5 last:border-r-0"
                    >
                      {r.primitive_title}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {previewRows.map((row, ri) => (
                  <tr key={ri} className="border-t">
                    {explicitRows.map((r) => (
                      <td
                        key={r.primitive_id}
                        className="max-w-[180px] truncate border-r px-2 py-1.5 text-grey6 last:border-r-0"
                      >
                        {row[r.column_index] ?? "—"}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <p className="text-xs text-grey5">
          {translate("QMS/INGESTION/FIELDS_MAPPED", {
            count: rows.length,
            scope: scopeId,
          })}
        </p>
        <button
          onClick={proceed}
          className="rounded-lg bg-yellow1 px-4 py-2 text-sm font-medium text-black transition-colors hover:bg-yellow5/30"
        >
          {translate("QMS/INGESTION/START_EXTRACTION_BTN")}
        </button>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Template Select Step — pick an ingestion template for the anchor scope.
//
// Pulls the active config's template list from the backend so ``status``
// (ready / missing_primitives) and ``missing`` refs are available.  Only
// ``ready`` templates are clickable; a template whose active config is
// missing required primitives is rendered disabled with the missing refs
// listed inline.  This is the use-time validation the user asked for —
// activation does not enforce primitives, selection does.
// ---------------------------------------------------------------------------

function TemplateSelectStep({
  anchorScopeId,
  selectedTemplateId,
  onSelect,
  onProceed,
}: {
  anchorScopeId: string
  selectedTemplateId: string | null
  onSelect: (id: string) => void
  onProceed: () => void
}) {
  const { translate } = useTranslation()
  // Registry list — anchors the template contract (id, resolutions, writes).
  const registered = listIngestionTemplatesForAnchor(anchorScopeId)

  // Backend-enriched status — tells us which of the above are ready.
  const { data: active } = useQuery({
    queryKey: ["active-templates"],
    queryFn: () => api.activeTemplates(),
  })

  const statusById = new Map<
    string,
    { status: "ready" | "missing_primitives"; missing: string[] }
  >()
  for (const t of active?.ingestion ?? []) {
    statusById.set(t.id, { status: t.status, missing: t.missing })
  }

  const entries = registered.map((t) => ({
    template: t,
    status: statusById.get(t.id)?.status ?? "missing_primitives",
    missing: statusById.get(t.id)?.missing ?? [
      "template not assigned in active config",
    ],
    // A template registered in the frontend but not assigned in the active
    // config also counts as "not ready" — the selection UI has nothing to
    // offer until the config opts in.
    assigned: statusById.has(t.id),
  }))

  const readyEntries = entries.filter((e) => e.assigned && e.status === "ready")

  useEffect(() => {
    if (
      readyEntries.length === 1 &&
      selectedTemplateId !== readyEntries[0].template.id
    ) {
      onSelect(readyEntries[0].template.id)
    }
  }, [anchorScopeId, readyEntries, selectedTemplateId, onSelect])

  if (entries.length === 0) {
    return (
      <div className="rounded-lg border border-grey4 bg-grey1 p-6 text-sm text-grey5">
        {translate("QMS/INGESTION/NO_TEMPLATE", { scope: anchorScopeId })}
      </div>
    )
  }

  return (
    <div>
      <p className="mb-4 text-sm text-grey5">
        {translate("QMS/INGESTION/SELECT_TEMPLATE")}
      </p>

      <div className="mb-4 space-y-2">
        {entries.map((e) => {
          const t = e.template
          const isActive = selectedTemplateId === t.id
          const disabled = !e.assigned || e.status !== "ready"
          return (
            <button
              key={t.id}
              onClick={() => {
                if (!disabled) onSelect(t.id)
              }}
              disabled={disabled}
              className={clsx(
                "w-full rounded-xl border-2 px-4 py-3 text-left transition-all",
                disabled
                  ? "cursor-not-allowed border-grey4 bg-grey1 opacity-60"
                  : isActive
                    ? "border-yellow1 bg-yellow5/10"
                    : "border-grey4 hover:border-yellow5 hover:bg-yellow5/10",
              )}
            >
              <div className="flex items-center gap-2">
                <div className="text-sm font-semibold text-white">
                  {t.label}
                </div>
                {disabled && (
                  <span className="rounded bg-warning/20 px-2 py-0.5 text-[10px] font-medium text-warning">
                    {translate("QMS/INGESTION/UNAVAILABLE")}
                  </span>
                )}
              </div>
              {t.description && (
                <div className="mt-0.5 text-xs text-grey5">{t.description}</div>
              )}
              <div className="font-mono mt-1 text-[10px] text-grey5">
                {t.id}
              </div>
              {disabled && e.missing.length > 0 && (
                <div className="mt-2 text-[11px] text-warning">
                  <div className="font-medium">
                    {translate("QMS/INGESTION/MISSING_FIELDS")}
                  </div>
                  <ul className="font-mono mt-0.5 space-y-0.5">
                    {e.missing.map((m) => (
                      <li key={m}>· {m}</li>
                    ))}
                  </ul>
                </div>
              )}
            </button>
          )
        })}
      </div>

      <div className="flex items-center justify-between">
        <p className="text-xs text-grey5">
          {translate("QMS/INGESTION/TEMPLATES_AVAILABLE", {
            ready: readyEntries.length,
            total: entries.length,
          })}
        </p>
        <button
          onClick={onProceed}
          disabled={!selectedTemplateId}
          className={clsx(
            "rounded-lg px-4 py-2 text-sm font-medium transition-colors",
            selectedTemplateId
              ? "bg-yellow1 text-black hover:bg-yellow5/30"
              : "cursor-not-allowed bg-grey3 text-grey5",
          )}
        >
          {translate("QMS/INGESTION/RUN_TEMPLATE")}
        </button>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// PDF Extraction Step — executes the selected ingestion template against
// live DB state and hands the resulting DiffState to the diff view.  The
// template encapsulates every literal (article numbers, CAS, values,
// traversal path); this component is the same for every template.
// ---------------------------------------------------------------------------

function PdfExtractionStep({
  templateId,
  anchorRecordId,
  anchorScopeId,
  fileName,
  onDone,
  onError,
}: {
  templateId: string
  anchorRecordId: string
  anchorScopeId: string
  fileName: string
  onDone: (diffState: DiffState) => void
  onError: (msg: string) => void
}) {
  const { translate } = useTranslation()
  const ran = useRef(false)
  const qc = useQueryClient()

  useEffect(() => {
    if (ran.current) return
    ran.current = true
    ;(async () => {
      try {
        const template = getIngestionTemplate(templateId)
        if (!template) {
          onError(
            translate("QMS/INGESTION/TEMPLATE_NOT_REGISTERED", {
              id: templateId,
            }),
          )
          return
        }
        const { primary, additional } = await executeIngestionTemplate(
          template,
          { scopeId: anchorScopeId, recordId: anchorRecordId, fileName },
          { qc },
        )
        onDone(primary)
        for (const [id, scopeDiff] of Object.entries(additional)) {
          useStore.getState().setActionScopeDiff(id, scopeDiff)
        }
      } catch (e) {
        console.error("[Template execution] Error:", e)
        onError(
          translate("QMS/INGESTION/TEMPLATE_EXEC_FAILED", { error: String(e) }),
        )
      }
    })()
  }, [])

  return (
    <div className="flex flex-col items-center justify-center gap-4 py-12">
      <div className="relative h-12 w-12">
        <span className="absolute inset-0 rounded-full border-2 border-purple1/30" />
        <span className="absolute inset-0 animate-spin rounded-full border-2 border-purple1 border-t-transparent" />
      </div>
      <div className="text-center">
        <p className="text-sm font-medium text-white">
          {translate("QMS/INGESTION/FRESHNESS_PREPARING")}
        </p>
        <p className="mt-1 text-xs text-grey5">
          {translate("QMS/INGESTION/FRESHNESS_LOADING")}
        </p>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Unconnected Records Panel — shown after pk_prefix hierarchy preview
// ---------------------------------------------------------------------------

function UnconnectedRecordsPanel({
  unconnected,
}: {
  unconnected: UnconnectedSummary
}) {
  const { translate } = useTranslation()
  const [expanded, setExpanded] = useState(false)
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set())
  const colHeaders = unconnected.headers ?? []

  function toggleGroup(label: string) {
    setExpandedGroups((prev) => {
      const next = new Set(prev)
      if (next.has(label)) next.delete(label)
      else next.add(label)
      return next
    })
  }

  return (
    <div className="overflow-hidden rounded-lg border border-warning bg-warning/10">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex w-full items-center justify-between px-4 py-3 text-left transition-colors hover:bg-warning/10"
      >
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-warning">
            {translate("QMS/INGESTION/UNCONNECTED", {
              count: unconnected.total,
            })}
          </span>
          <span className="text-xs text-warning">
            {translate("QMS/INGESTION/NO_PREFIX")}
          </span>
        </div>
        <span className="text-xs text-warning">
          {expanded
            ? translate("QMS/INGESTION/COLLAPSE_BTN")
            : translate("QMS/INGESTION/EXPAND_BTN")}
        </span>
      </button>

      {expanded && (
        <div className="space-y-1 border-t border-warning px-4 py-3">
          <p className="mb-3 text-xs text-warning">
            {translate("QMS/INGESTION/UNCONNECTED_HELP")}
          </p>

          {unconnected.groups.map((group) => (
            <div
              key={group.label}
              className="rounded border border-warning bg-grey2"
            >
              <button
                onClick={() => toggleGroup(group.label)}
                className="flex w-full items-center justify-between px-3 py-2 text-left transition-colors hover:bg-warning/10"
              >
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-grey6">
                    {group.label}
                  </span>
                  <span className="rounded bg-grey3 px-1.5 py-0.5 text-[10px] text-grey5">
                    {group.count}
                  </span>
                </div>
                <span className="text-[10px] text-grey5">
                  {expandedGroups.has(group.label) ? "−" : "+"}
                </span>
              </button>

              {expandedGroups.has(group.label) && group.samples.length > 0 && (
                <div className="overflow-x-auto border-t border-warning">
                  <table className="min-w-max text-xs">
                    <thead>
                      <tr className="border-b border-warning">
                        {colHeaders.map((h, i) => (
                          <th
                            key={i}
                            className="whitespace-nowrap bg-warning/10 px-2 py-1.5 text-left text-[10px] font-semibold uppercase text-grey5"
                          >
                            {h ||
                              translate("QMS/COLUMN_MAPPING/COLUMN_FALLBACK", {
                                index: i + 1,
                              })}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {group.samples.map((rec) => (
                        <tr
                          key={rec.pk}
                          className="border-b border-warning hover:bg-warning/10"
                        >
                          {(rec.row ?? []).map((cell, i) => (
                            <td
                              key={i}
                              className="max-w-[200px] truncate whitespace-nowrap px-2 py-1 text-grey5"
                              title={cell}
                            >
                              {cell || <span className="text-grey5">—</span>}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {group.count > group.samples.length && (
                    <p className="px-2 py-1.5 text-xs italic text-grey5">
                      {translate("QMS/INGESTION/AND_MORE", {
                        count: group.count - group.samples.length,
                      })}
                    </p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Hierarchy Step — configure and preview hierarchical group extraction
// ---------------------------------------------------------------------------

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function HierarchyStep({
  scopeId,
  sheet,
  detected,
  mappings,
  onProceed,
  onError,
}: {
  scopeId: string
  sheet: ParsedSheet | null
  detected: DetectOut
  mappings: UserMapping[]
  onProceed: () => void
  onError: (msg: string) => void
}) {
  const { translate } = useTranslation()
  const headers = sheet?.headers ?? []
  const rows = sheet?.rows ?? []

  // Find hierarchical primitives
  const hierPrims = [
    ...detected.explicit.filter((e) => e.is_hierarchical),
    ...detected.implicit.filter((e) => e.is_hierarchical),
  ]
  const hierCandidates = detected.hierarchy_candidates ?? []
  const parentRefCandidates = hierCandidates.filter(
    (c) => c.role === "parent_ref",
  )
  const levelCandidates = hierCandidates.filter((c) => c.role === "level")
  const pkPrefixCandidates = hierCandidates.filter(
    (c) => c.role === "pk_prefix",
  )

  // Mode is defined by the primitive's group_structure config.
  // Fall back to auto-detection from candidates if not set.
  const mode: "horizontal" | "flat" | "parent_ref" | "pk_prefix" = (() => {
    const prim = hierPrims[0]
    if (prim?.group_structure) return prim.group_structure
    if (pkPrefixCandidates.length > 0) return "pk_prefix"
    if (parentRefCandidates.length > 0) return "parent_ref"
    if (levelCandidates.length >= 2) return "horizontal"
    return "flat"
  })()

  // --- Horizontal mode state ---
  const [levelColumns, setLevelColumns] = useState<number[]>(() =>
    levelCandidates.map((c) => c.column_index),
  )

  // --- Flat mode state ---
  const [nameColumn, setNameColumn] = useState(0)
  const [parentColumn, setParentColumn] = useState(1)

  // --- Parent-ref mode state ---
  const pkExplicit = detected.explicit.find(
    (e) => e.primitive_scope_id === scopeId && e.primitive_type === "string",
  )
  const [prPkColumn, setPrPkColumn] = useState(
    () => pkExplicit?.column_index ?? 1,
  )
  const [prNameColumn, setPrNameColumn] = useState(
    () => (pkExplicit?.column_index ?? 1) + 1,
  )
  // Ordered reference columns (auto-detected from parent_ref candidates)
  const [prRefColumns, setPrRefColumns] = useState<number[]>(() =>
    parentRefCandidates.map((c) => c.column_index),
  )
  // Level labels: prefer deriving from a detected level column (e.g. Materialgruppe),
  // fall back to column header names
  const autoLabelCol = levelCandidates[0]?.column_index ?? -1
  const [prLevelLabels, setPrLevelLabels] = useState<string[]>(() => {
    const numLevels = parentRefCandidates.length + 1

    // Try deriving from a level column's distinct values
    if (autoLabelCol >= 0) {
      const seen = new Set<string>()
      const labels: string[] = []
      for (const row of rows) {
        const v = (row[autoLabelCol] ?? "").trim()
        if (v && !seen.has(v)) {
          seen.add(v)
          labels.push(v)
        }
      }
      if (labels.length >= numLevels) return labels.slice(0, numLevels)
      // Pad if not enough
      while (labels.length < numLevels)
        labels.push(
          translate("QMS/INGESTION/LEVEL_FALLBACK", { n: labels.length }),
        )
      return labels
    }

    // Fallback: strip suffixes from ref column headers + generic leaf label
    const fromHeaders = parentRefCandidates.map((c) =>
      c.header
        .replace(/_matnr$/i, "")
        .replace(/_nr$/i, "")
        .replace(/_id$/i, "")
        .replace(/_ref$/i, "")
        .trim(),
    )
    while (fromHeaders.length < numLevels)
      fromHeaders.push(
        translate("QMS/INGESTION/LEVEL_FALLBACK", { n: fromHeaders.length }),
      )
    return fromHeaders
  })
  const [prLabelSource, setPrLabelSource] = useState<"column" | "manual">(() =>
    autoLabelCol >= 0 ? "column" : "manual",
  )
  const [prLabelColumn, setPrLabelColumn] = useState(() => autoLabelCol)

  // --- Pk-prefix mode state ---
  const pkPrefixCandidate = pkPrefixCandidates[0]
  const [ppPkColumn, setPpPkColumn] = useState(
    () => pkPrefixCandidate?.column_index ?? pkExplicit?.column_index ?? 1,
  )
  const [ppNameColumn, setPpNameColumn] = useState(
    () => (pkExplicit?.column_index ?? 1) + 1,
  )
  const [ppSeparator, setPpSeparator] = useState(
    () => pkPrefixCandidate?.separator ?? "-",
  )
  const [ppLevelLabelColumn, setPpLevelLabelColumn] = useState<number | null>(
    () => levelCandidates[0]?.column_index ?? null,
  )

  const [preview, setPreview] = useState<BuildHierarchyOut | null>(null)
  const [loading, setLoading] = useState(false)

  const primId = hierPrims[0]?.primitive_id

  // If no hierarchical primitives, skip this step via effect (not during render)
  const skipRef = useRef(false)
  useEffect(() => {
    if (!primId && !skipRef.current) {
      skipRef.current = true
      onProceed()
    }
  }, [primId, onProceed])

  if (!primId) {
    return <Spinner label={translate("QMS/INGESTION/HIERARCHY_SKIP")} />
  }

  // Derive level labels from a column's distinct values
  function deriveLabelsFromColumn(colIdx: number) {
    setPrLabelColumn(colIdx)
    const seen = new Set<string>()
    const labels: string[] = []
    for (const row of rows) {
      const v = (row[colIdx] ?? "").trim()
      if (v && !seen.has(v)) {
        seen.add(v)
        labels.push(v)
      }
    }
    // Ensure we have enough labels for ref_columns + 1
    while (labels.length < prRefColumns.length + 1)
      labels.push(
        translate("QMS/INGESTION/LEVEL_FALLBACK", { n: labels.length }),
      )
    setPrLevelLabels(labels.slice(0, prRefColumns.length + 1))
    setPreview(null)
  }

  function buildPayload() {
    return {
      mode,
      rows,
      headers,
      horizontal:
        mode === "horizontal"
          ? { primitive_id: primId!, level_columns: levelColumns }
          : undefined,
      flat:
        mode === "flat"
          ? {
              primitive_id: primId!,
              name_column: nameColumn,
              parent_column: parentColumn,
            }
          : undefined,
      parent_ref:
        mode === "parent_ref"
          ? {
              primitive_id: primId!,
              pk_column: prPkColumn,
              ref_columns: prRefColumns,
              name_column: prNameColumn,
              level_labels: prLevelLabels,
            }
          : undefined,
      pk_prefix:
        mode === "pk_prefix"
          ? {
              primitive_id: primId!,
              pk_column: ppPkColumn,
              separator: ppSeparator,
              name_column: ppNameColumn,
              level_label_column: ppLevelLabelColumn,
              level_labels: [],
            }
          : undefined,
    }
  }

  async function buildPreview() {
    if (!primId) return
    setLoading(true)
    setPreview(null)
    try {
      const result = await api.buildHierarchy(scopeId, primId, buildPayload())
      setPreview(result)
    } catch (e) {
      onError(String(e))
    } finally {
      setLoading(false)
    }
  }

  async function commitAndProceed() {
    if (!primId) return
    setLoading(true)
    try {
      await api.commitHierarchy(scopeId, primId, buildPayload())
      onProceed()
    } catch (e) {
      onError(String(e))
    } finally {
      setLoading(false)
    }
  }

  function addLevelColumn() {
    const next = headers.findIndex((_, i) => !levelColumns.includes(i))
    if (next >= 0) setLevelColumns([...levelColumns, next])
  }

  function removeLevelColumn(idx: number) {
    setLevelColumns(levelColumns.filter((_, i) => i !== idx))
  }

  function moveLevelColumn(idx: number, dir: -1 | 1) {
    const newCols = [...levelColumns]
    const target = idx + dir
    if (target < 0 || target >= newCols.length) return
    ;[newCols[idx], newCols[target]] = [newCols[target], newCols[idx]]
    setLevelColumns(newCols)
  }

  function moveRefColumn(idx: number, dir: -1 | 1) {
    const newCols = [...prRefColumns]
    const target = idx + dir
    if (target < 0 || target >= newCols.length) return
    ;[newCols[idx], newCols[target]] = [newCols[target], newCols[idx]]
    setPrRefColumns(newCols)
    setPreview(null)
  }

  return (
    <div className="flex flex-col gap-4">
      <div>
        <p className="mb-1 text-sm text-grey5">
          {translate("QMS/INGESTION/HIERARCHY_CONFIGURE", {
            prim: hierPrims[0]?.primitive_title ?? "",
          })}
        </p>
        <p className="text-xs text-grey5">
          {translate("QMS/INGESTION/HIERARCHY_DEFINE")}
        </p>
      </div>

      {/* Mode-specific config */}
      {mode === "parent_ref" && (
        <div className="space-y-3 rounded-lg border bg-grey3/50 p-3">
          <p className="text-xs text-grey5">
            {translate("QMS/INGESTION/PARENT_REF_INTRO_1")}{" "}
            <strong>{translate("QMS/INGESTION/REF_COLUMNS_WORD")}</strong>
            {translate("QMS/INGESTION/PARENT_REF_INTRO_2")}{" "}
            {translate("QMS/INGESTION/HIERARCHY_EACH_COL")}{" "}
            {translate("QMS/INGESTION/PARENT_REF_ORDER_HINT")}
          </p>

          {/* PK + Name column selectors */}
          <div className="flex flex-wrap items-end gap-3">
            <label className="flex flex-col gap-1">
              <span className="text-[10px] font-semibold uppercase text-grey5">
                {translate("QMS/INGESTION/PK_COLUMN_LABEL")}
              </span>
              <select
                value={prPkColumn}
                onChange={(e) => {
                  setPrPkColumn(Number(e.target.value))
                  setPreview(null)
                }}
                className="rounded border bg-grey2 px-1.5 py-1 text-xs text-grey6"
              >
                {headers.map((h, i) => (
                  <option key={i} value={i}>
                    {h ||
                      translate("QMS/COLUMN_MAPPING/COLUMN_FALLBACK", {
                        index: i + 1,
                      })}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-[10px] font-semibold uppercase text-grey5">
                {translate("QMS/INGESTION/NODE_DISPLAY_NAME")}
              </span>
              <select
                value={prNameColumn}
                onChange={(e) => {
                  setPrNameColumn(Number(e.target.value))
                  setPreview(null)
                }}
                className="rounded border bg-grey2 px-1.5 py-1 text-xs text-grey6"
              >
                {headers.map((h, i) => (
                  <option key={i} value={i}>
                    {h ||
                      translate("QMS/COLUMN_MAPPING/COLUMN_FALLBACK", {
                        index: i + 1,
                      })}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {/* Reference columns — ordered root→leaf */}
          <div>
            <p className="mb-1 text-[10px] font-semibold uppercase text-grey5">
              {translate("QMS/INGESTION/REF_COLUMNS_HEADING")}
            </p>
            <div className="mb-2 flex flex-col gap-1">
              {prRefColumns.map((colIdx, i) => (
                <div
                  key={i}
                  className="flex items-center gap-2 rounded border bg-grey2 px-2 py-1.5"
                >
                  <span className="font-mono w-6 text-[10px] text-grey5">
                    R{i}
                  </span>
                  <select
                    value={colIdx}
                    onChange={(e) => {
                      const newCols = [...prRefColumns]
                      newCols[i] = Number(e.target.value)
                      setPrRefColumns(newCols)
                      setPreview(null)
                    }}
                    className="flex-1 rounded border bg-grey2 px-1.5 py-0.5 text-xs text-grey6"
                  >
                    {headers.map((h, hi) => (
                      <option key={hi} value={hi}>
                        {h ||
                          translate("QMS/COLUMN_MAPPING/COLUMN_FALLBACK", {
                            index: hi + 1,
                          })}
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={() => moveRefColumn(i, -1)}
                    disabled={i === 0}
                    className="text-xs text-grey5 hover:text-grey6 disabled:opacity-20"
                  >
                    ^
                  </button>
                  <button
                    onClick={() => moveRefColumn(i, 1)}
                    disabled={i === prRefColumns.length - 1}
                    className="text-xs text-grey5 hover:text-grey6 disabled:opacity-20"
                  >
                    v
                  </button>
                  <button
                    onClick={() => {
                      setPrRefColumns(prRefColumns.filter((_, j) => j !== i))
                      setPreview(null)
                    }}
                    className="text-sm text-grey5 hover:text-red1"
                  >
                    x
                  </button>
                </div>
              ))}
            </div>
            <button
              onClick={() => {
                const next = headers.findIndex(
                  (_, i) => !prRefColumns.includes(i),
                )
                if (next >= 0) {
                  setPrRefColumns([...prRefColumns, next])
                  setPreview(null)
                }
              }}
              className="text-xs font-medium text-yellow1 hover:text-yellow1"
            >
              {translate("QMS/INGESTION/ADD_REF_COL")}
            </button>
          </div>

          {/* Level labels */}
          <div>
            <div className="mb-1 flex items-center gap-2">
              <span className="text-[10px] font-semibold uppercase text-grey5">
                {translate("QMS/INGESTION/LEVEL_LABELS")}
              </span>
              <button
                onClick={() => {
                  if (prLabelSource === "column") {
                    setPrLabelSource("manual")
                  } else {
                    setPrLabelSource("column")
                    if (prLabelColumn >= 0)
                      deriveLabelsFromColumn(prLabelColumn)
                  }
                }}
                className="text-[10px] text-yellow1 hover:text-yellow1"
              >
                {prLabelSource === "column"
                  ? translate("QMS/INGESTION/LEVEL_LABELS_MANUAL")
                  : translate("QMS/INGESTION/LEVEL_LABELS_FROM_COLUMN")}
              </button>
            </div>

            {prLabelSource === "column" && (
              <div className="mb-2 flex items-center gap-2">
                <select
                  value={prLabelColumn}
                  onChange={(e) =>
                    deriveLabelsFromColumn(Number(e.target.value))
                  }
                  className="rounded border bg-grey2 px-1.5 py-1 text-xs text-grey6"
                >
                  <option value={-1}>
                    {translate("QMS/INGESTION/SELECT_COL")}
                  </option>
                  {headers.map((h, i) => (
                    <option key={i} value={i}>
                      {h ||
                        translate("QMS/COLUMN_MAPPING/COLUMN_FALLBACK", {
                          index: i + 1,
                        })}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="flex flex-col gap-1">
              {prLevelLabels.map((lbl, i) => (
                <div
                  key={i}
                  className="flex items-center gap-2 rounded border bg-grey2 px-2 py-1.5"
                >
                  <span className="font-mono w-6 text-[10px] text-grey5">
                    L{i}
                  </span>
                  <input
                    type="text"
                    value={lbl}
                    onChange={(e) => {
                      const updated = [...prLevelLabels]
                      updated[i] = e.target.value
                      setPrLevelLabels(updated)
                    }}
                    className="flex-1 rounded border bg-grey2 px-1.5 py-0.5 text-xs text-grey6"
                  />
                  {i === 0 && (
                    <span className="rounded bg-success/10 px-1.5 py-0.5 text-[10px] font-medium text-success">
                      ROOT
                    </span>
                  )}
                  {i === prLevelLabels.length - 1 && (
                    <span className="font-mono text-[10px] text-grey5">
                      LEAF
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {mode === "horizontal" && (
        <div className="rounded-lg border bg-grey3/50 p-3">
          <p className="mb-2 text-xs text-grey5">
            {translate("QMS/INGESTION/HORIZONTAL_HIERARCHY_HINT")}
          </p>
          <div className="mb-2 flex flex-col gap-1.5">
            {levelColumns.map((colIdx, i) => (
              <div
                key={i}
                className="flex items-center gap-2 rounded border bg-grey2 px-2 py-1.5"
              >
                <span className="font-mono w-6 text-[10px] text-grey5">
                  L{i}
                </span>
                <select
                  value={colIdx}
                  onChange={(e) => {
                    const newCols = [...levelColumns]
                    newCols[i] = Number(e.target.value)
                    setLevelColumns(newCols)
                  }}
                  className="flex-1 rounded border bg-grey2 px-1.5 py-0.5 text-xs text-grey6"
                >
                  {headers.map((h, hi) => (
                    <option key={hi} value={hi}>
                      {h ||
                        translate("QMS/COLUMN_MAPPING/COLUMN_FALLBACK", {
                          index: hi + 1,
                        })}
                    </option>
                  ))}
                </select>
                <button
                  onClick={() => moveLevelColumn(i, -1)}
                  disabled={i === 0}
                  className="text-xs text-grey5 hover:text-grey6 disabled:opacity-20"
                >
                  ^
                </button>
                <button
                  onClick={() => moveLevelColumn(i, 1)}
                  disabled={i === levelColumns.length - 1}
                  className="text-xs text-grey5 hover:text-grey6 disabled:opacity-20"
                >
                  v
                </button>
                <button
                  onClick={() => removeLevelColumn(i)}
                  className="text-sm text-grey5 hover:text-red1"
                >
                  x
                </button>
              </div>
            ))}
          </div>
          <button
            onClick={addLevelColumn}
            className="text-xs font-medium text-yellow1 hover:text-yellow1"
          >
            {translate("QMS/INGESTION/ADD_LEVEL")}
          </button>
        </div>
      )}

      {mode === "flat" && (
        <div className="rounded-lg border bg-grey3/50 p-3">
          <p className="mb-2 text-xs text-grey5">
            {translate("QMS/INGESTION/GROUP_NAME_HELP")}
          </p>
          <div className="flex items-center gap-3">
            <label className="flex flex-col gap-1">
              <span className="text-[10px] font-semibold uppercase text-grey5">
                {translate("QMS/INGESTION/GROUP_NAME_LABEL")}
              </span>
              <select
                value={nameColumn}
                onChange={(e) => setNameColumn(Number(e.target.value))}
                className="rounded border bg-grey2 px-1.5 py-1 text-xs text-grey6"
              >
                {headers.map((h, i) => (
                  <option key={i} value={i}>
                    {h ||
                      translate("QMS/COLUMN_MAPPING/COLUMN_FALLBACK", {
                        index: i + 1,
                      })}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-[10px] font-semibold uppercase text-grey5">
                {translate("QMS/INGESTION/PARENT_GROUP")}
              </span>
              <select
                value={parentColumn}
                onChange={(e) => setParentColumn(Number(e.target.value))}
                className="rounded border bg-grey2 px-1.5 py-1 text-xs text-grey6"
              >
                {headers.map((h, i) => (
                  <option key={i} value={i}>
                    {h ||
                      translate("QMS/COLUMN_MAPPING/COLUMN_FALLBACK", {
                        index: i + 1,
                      })}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>
      )}

      {mode === "pk_prefix" && (
        <div className="space-y-3 rounded-lg border bg-grey3/50 p-3">
          <p className="text-xs text-grey5">
            {translate("QMS/INGESTION/PREFIX_HELP")}
          </p>

          {/* PK + Name + Separator */}
          <div className="flex flex-wrap items-end gap-3">
            <label className="flex flex-col gap-1">
              <span className="text-[10px] font-semibold uppercase text-grey5">
                {translate("QMS/INGESTION/PK_COLUMN_LABEL")}
              </span>
              <select
                value={ppPkColumn}
                onChange={(e) => {
                  setPpPkColumn(Number(e.target.value))
                  setPreview(null)
                }}
                className="rounded border bg-grey2 px-1.5 py-1 text-xs text-grey6"
              >
                {headers.map((h, i) => (
                  <option key={i} value={i}>
                    {h ||
                      translate("QMS/COLUMN_MAPPING/COLUMN_FALLBACK", {
                        index: i + 1,
                      })}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-[10px] font-semibold uppercase text-grey5">
                {translate("QMS/INGESTION/NODE_DISPLAY_NAME")}
              </span>
              <select
                value={ppNameColumn}
                onChange={(e) => {
                  setPpNameColumn(Number(e.target.value))
                  setPreview(null)
                }}
                className="rounded border bg-grey2 px-1.5 py-1 text-xs text-grey6"
              >
                {headers.map((h, i) => (
                  <option key={i} value={i}>
                    {h ||
                      translate("QMS/COLUMN_MAPPING/COLUMN_FALLBACK", {
                        index: i + 1,
                      })}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-[10px] font-semibold uppercase text-grey5">
                {translate("QMS/INGESTION/SEPARATOR_LABEL")}
              </span>
              <input
                type="text"
                value={ppSeparator}
                onChange={(e) => {
                  setPpSeparator(e.target.value)
                  setPreview(null)
                }}
                className="font-mono w-12 rounded border bg-grey2 px-1.5 py-1 text-center text-xs text-grey6"
                maxLength={3}
              />
            </label>
          </div>

          {/* Level label column (optional) */}
          <div className="flex flex-wrap items-end gap-3">
            <label className="flex flex-col gap-1">
              <span className="text-[10px] font-semibold uppercase text-grey5">
                {translate("QMS/INGESTION/LEVEL_LABELS_FROM_COLUMN_HEADING")}{" "}
                <span className="text-grey5">(optional)</span>
              </span>
              <select
                value={ppLevelLabelColumn ?? -1}
                onChange={(e) => {
                  const v = Number(e.target.value)
                  setPpLevelLabelColumn(v >= 0 ? v : null)
                  setPreview(null)
                }}
                className="rounded border bg-grey2 px-1.5 py-1 text-xs text-grey6"
              >
                <option value={-1}>{translate("QMS/COMMON/NONE_AUTO")}</option>
                {headers.map((h, i) => (
                  <option key={i} value={i}>
                    {h ||
                      translate("QMS/COLUMN_MAPPING/COLUMN_FALLBACK", {
                        index: i + 1,
                      })}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>
      )}

      {/* Preview / Confirm buttons */}
      <div className="flex items-center gap-2">
        <button
          onClick={buildPreview}
          disabled={
            loading ||
            (mode === "horizontal" && levelColumns.length < 1) ||
            (mode === "parent_ref" && prRefColumns.length < 1)
          }
          className="rounded-lg bg-grey3 px-4 py-2 text-sm font-medium text-grey6 transition-colors hover:bg-grey4 disabled:opacity-40"
        >
          {loading
            ? translate("QMS/INGESTION/BUILDING")
            : translate("QMS/INGESTION/HIERARCHY_PREVIEW")}
        </button>
        {preview && (
          <button
            onClick={commitAndProceed}
            disabled={loading}
            className="rounded-lg bg-yellow1 px-4 py-2 text-sm font-medium text-black transition-colors hover:bg-yellow5/30 disabled:opacity-40"
          >
            {translate("QMS/INGESTION/CONFIRM_CONTINUE")}
          </button>
        )}
        {!preview && (
          <button
            onClick={onProceed}
            className="ml-auto text-xs text-grey5 hover:text-grey5"
          >
            {translate("QMS/INGESTION/SKIP_HIERARCHY")}
          </button>
        )}
      </div>

      {/* Preview tree */}
      {preview && (
        <>
          <HierarchyPreview
            tree={preview.tree}
            warnings={preview.warnings}
            rows={rows}
            headers={headers}
            mappings={mappings}
          />
          {preview.unconnected && preview.unconnected.total > 0 && (
            <UnconnectedRecordsPanel unconnected={preview.unconnected} />
          )}
        </>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Extraction Run Step
// ---------------------------------------------------------------------------

function ExtractionRunStep({
  scopeId,
  activeSheet,
  mappings,
  pkPrimitiveId,
  anchorChoice,
  onDone,
  onError,
}: {
  scopeId: string
  activeSheet: ParsedSheet | null
  mappings: UserMapping[]
  pkPrimitiveId: string | null
  anchorChoice?: AnchorChoice
  onDone: (extractions: ExtractionEntry[], anchored: AnchoredRecord[]) => void
  onError: (msg: string) => void
}) {
  const { translate } = useTranslation()
  const [progress, setProgress] = useState("")
  const qc = useQueryClient()
  const started = useRef(false)

  useEffect(() => {
    if (started.current) return
    started.current = true
    ;(async () => {
      if (!activeSheet || mappings.length === 0) {
        onDone([], [])
        return
      }

      // --- Run anchor first using user-confirmed PK column ---
      setProgress(translate("QMS/INGESTION/PROGRESS_ROWS"))
      const pkMapping = pkPrimitiveId
        ? mappings.find((m) => m.primitive_id === pkPrimitiveId)
        : null
      const pkColOverride = pkMapping ? pkMapping.column_index : null

      const anchorPayload =
        anchorChoice?.mode === "mapping" && anchorChoice.mappingPrimitiveId
          ? {
              file_type: "excel" as const,
              rows: activeSheet.rows,
              headers: activeSheet.headers,
              anchor_mode: "mapping" as const,
              mapping_primitive_id: anchorChoice.mappingPrimitiveId,
              mapping_col_override:
                anchorChoice.mappingColumnIndex >= 0
                  ? anchorChoice.mappingColumnIndex
                  : null,
            }
          : {
              file_type: "excel" as const,
              rows: activeSheet.rows,
              headers: activeSheet.headers,
              pk_col_override: pkColOverride,
            }
      let anchorOut
      try {
        anchorOut = await api.ingestAnchor(scopeId, anchorPayload)
      } catch (e) {
        onError(formatAnchorError(e, translate))
        return
      }

      // Build anchored records from anchor result
      const norm = (v: string) => v.trim().toLowerCase()
      const existingAnchored: AnchoredRecord[] = anchorOut.matches.map((m) => ({
        record_id: m.record_id,
        pk_value: m.pk_value,
        name_value: m.name_value,
        row_index: m.row_index,
        is_new: false,
        additional_row_indices: [],
      }))
      const existingByPk = new Map<string, AnchoredRecord>()
      for (const a of existingAnchored) existingByPk.set(norm(a.pk_value), a)

      const newRowsByPk = new Map<string, NewRowCandidate[]>()
      for (const nr of anchorOut.new_rows ?? []) {
        const key = norm(nr.pk_value)
        if (!newRowsByPk.has(key)) newRowsByPk.set(key, [])
        newRowsByPk.get(key)!.push(nr)
      }
      const newAnchored: AnchoredRecord[] = []
      for (const [key, anchorRows] of newRowsByPk) {
        const existing = existingByPk.get(key)
        if (existing) {
          existing.additional_row_indices = [
            ...(existing.additional_row_indices ?? []),
            ...anchorRows.map((r) => r.row_index),
          ]
        } else {
          const [primary, ...extras] = anchorRows
          newAnchored.push({
            record_id: `new::${primary.row_index}`,
            pk_value: primary.pk_value,
            name_value: null,
            row_index: primary.row_index,
            is_new: true,
            additional_row_indices: extras.map((r) => r.row_index),
          })
        }
      }
      const anchored = [...existingAnchored, ...newAnchored]

      if (anchored.length === 0) {
        onDone([], [])
        return
      }

      const extractions: ExtractionEntry[] = []
      const rows = activeSheet.rows
      const scalarMappings = mappings.filter(
        (m) => m.primitive_type !== "relation",
      )
      const relationMappings = mappings.filter(
        (m) => m.primitive_type === "relation",
      )

      // Pre-fetch cached records for existing-value lookup
      // Use fetchQuery as fallback so the first extraction after a page refresh
      // always has records available (getQueryData may be empty before PivotTable mounts).
      const cachedPage =
        qc.getQueryData<{
          records: {
            id: string
            values: Record<
              string,
              { value: string | null; source: string; updated_at?: string }
            >
          }[]
          scope: { primitives: { id: string; name: string }[] }
        }>(["records", scopeId]) ??
        (await qc.fetchQuery({
          queryKey: ["records", scopeId],
          queryFn: () => api.listRecords(scopeId),
        }))

      // --- Scalar extraction (frontend) ---
      setProgress(translate("QMS/INGESTION/PROGRESS_EXTRACTING"))
      for (const a of anchored) {
        const rowIndices = [
          a.row_index,
          ...(a.additional_row_indices ?? []),
        ].filter((i): i is number => i !== null)
        for (const m of scalarMappings) {
          const rawValues = rowIndices
            .map((ri) => (rows[ri]?.[m.column_index] ?? "").trim())
            .filter((v) => v !== "")

          if (rawValues.length === 0) continue

          // Deduplicate: use the first unique value; track conflicts
          const unique = [...new Set(rawValues)]
          const value = unique[0]
          const conflictValues = unique.length > 1 ? unique.slice(1) : undefined

          // Normalize numbers: comma → dot
          let normalized = value
          if (m.primitive_type === "number") {
            normalized = value.replace(/,/g, ".")
          }

          // Look up existing DB value for source/timestamp
          let existingValue: string | null | undefined
          let existingSource: string | undefined
          let existingUpdatedAt: string | undefined
          const cachedRec = cachedPage?.records.find(
            (r) => r.id === a.record_id,
          )
          if (cachedRec) {
            const primName = cachedPage?.scope.primitives.find(
              (p) => p.id === m.primitive_id,
            )?.name
            const cell = primName ? cachedRec.values[primName] : undefined
            if (cell?.value) {
              existingValue = cell.value
              existingSource = cell.source
              existingUpdatedAt = cell.updated_at
            }
          }
          // Fallback: secondary scope values from rollup state
          if (existingValue === undefined) {
            const rsVals = useStore.getState().rollupState?.values
            const rollupEntry = rsVals?.[a.record_id]?.[m.primitive_id]
            if (rollupEntry?.value) {
              existingValue = rollupEntry.value
              existingSource = "rollup"
              existingUpdatedAt = rollupEntry.updated_at ?? undefined
            }
          }

          extractions.push({
            record_id: a.record_id,
            primitive_id: m.primitive_id,
            primitive_scope_id: m.primitive_scope_id,
            primitive_title: m.primitive_title,
            primitive_type: m.primitive_type,
            value: normalized,
            raw_value: value !== normalized ? value : null,
            needs_cleaning: false,
            confidence: 0.9,
            extraction_type: "explicit",
            related_scope_id: null,
            accepted: true,
            conflict_values: conflictValues,
            existing_value: existingValue ?? undefined,
            existing_source: existingSource,
            existing_updated_at: existingUpdatedAt,
          })
        }
      }

      // --- Relation resolution (frontend, exact PK lookup) ---
      // Track new secondary scope records created for unmatched relation values
      const secondaryNewRecords = new Map<string, Map<string, string>>() // scopeId → pkValue → provisionalId
      // PK→recordId lookup for each related scope (hoisted for secondary extraction)
      const scopeLookups = new Map<string, Map<string, string>>()

      if (relationMappings.length > 0) {
        setProgress(translate("QMS/INGESTION/PROGRESS_LINKS"))
        for (const m of relationMappings) {
          const relScopeId = m.related_scope_id
          if (!relScopeId || scopeLookups.has(relScopeId)) continue
          try {
            const cached =
              qc.getQueryData<RecordsPage>(["records", relScopeId]) ??
              (await qc.fetchQuery({
                queryKey: ["records", relScopeId],
                queryFn: () => api.listRecords(relScopeId),
              }))
            if (!cached) continue
            const pkPrim = cached.scope.primitives.find((p) => p.is_pk)
            if (!pkPrim) continue
            const lookup = new Map<string, string>()
            for (const rec of cached.records) {
              const pkVal = rec.values[pkPrim.name]?.value
              if (pkVal != null) {
                lookup.set(pkVal.trim(), rec.id)
                lookup.set(pkVal.trim().toLowerCase(), rec.id) // case-insensitive fallback
              }
            }
            scopeLookups.set(relScopeId, lookup)
          } catch (e) {
            console.warn(`Failed to fetch related scope ${relScopeId}:`, e)
          }
        }

        for (const a of anchored) {
          const rowIndices = [
            a.row_index,
            ...(a.additional_row_indices ?? []),
          ].filter((i): i is number => i !== null)
          for (const m of relationMappings) {
            const relScopeId = m.related_scope_id
            if (!relScopeId) continue
            const lookup =
              scopeLookups.get(relScopeId) ?? new Map<string, string>()

            // Collect all unique raw values across rows for this mapping
            // Split comma-separated values so "A, B" resolves to two targets
            const rawValues = new Set<string>()
            for (const ri of rowIndices) {
              const cell = (rows[ri]?.[m.column_index] ?? "").trim()
              if (cell === "") continue
              for (const part of cell.split(",")) {
                const v = part.trim()
                if (v !== "") rawValues.add(v)
              }
            }

            const resolvedTargets = new Set<string>()

            for (const raw of rawValues) {
              // Exact match first, then case-insensitive
              let targetId = lookup.get(raw) ?? lookup.get(raw.toLowerCase())

              // Cross-scope: create new record for unmatched values
              if (!targetId) {
                // Check if we already created a provisional record for this PK
                if (!secondaryNewRecords.has(relScopeId))
                  secondaryNewRecords.set(relScopeId, new Map())
                const scopeNewMap = secondaryNewRecords.get(relScopeId)!
                const normRaw = raw.trim().toLowerCase()
                if (scopeNewMap.has(normRaw)) {
                  targetId = scopeNewMap.get(normRaw)!
                } else {
                  targetId = `new::${relScopeId}::${raw.trim()}`
                  scopeNewMap.set(normRaw, targetId)
                  // Add to lookup so subsequent references resolve
                  lookup.set(raw.trim(), targetId)
                  lookup.set(normRaw, targetId)
                }
              }

              if (targetId) {
                resolvedTargets.add(targetId)
              }
            }

            // Emit one extraction per resolved target
            for (const targetId of resolvedTargets) {
              extractions.push({
                record_id: a.record_id,
                primitive_id: m.primitive_id,
                primitive_scope_id: m.primitive_scope_id,
                primitive_title: m.primitive_title,
                primitive_type: "relation",
                value: targetId,
                raw_value: null,
                needs_cleaning: false,
                confidence: 1,
                extraction_type: "explicit",
                related_scope_id: relScopeId,
                accepted: true,
                mapped_id: m.is_mapping_title
                  ? ([...rawValues].find((raw) => {
                      const tid =
                        lookup.get(raw) ?? lookup.get(raw.toLowerCase())
                      return tid === targetId
                    }) ?? null)
                  : null,
              })
            }

            // Document has no values for this relation — emit empty marker so
            // diff logic can detect removals of existing DB links
            if (rawValues.size === 0 && resolvedTargets.size === 0) {
              extractions.push({
                record_id: a.record_id,
                primitive_id: m.primitive_id,
                primitive_scope_id: m.primitive_scope_id,
                primitive_title: m.primitive_title,
                primitive_type: "relation",
                value: null,
                raw_value: null,
                needs_cleaning: false,
                confidence: 1,
                extraction_type: "explicit",
                related_scope_id: relScopeId,
                accepted: true,
              })
            }
          }
        }
      }

      // --- Secondary scope scalar extraction ---
      // For each secondary scope that has column mappings, extract scalar values
      // into the resolved (existing or new) secondary records.
      const secondaryScopeMappings = mappings.filter(
        (m) =>
          m.primitive_scope_id !== scopeId && m.primitive_type !== "relation",
      )
      if (secondaryScopeMappings.length > 0) {
        setProgress(translate("QMS/INGESTION/PROGRESS_SECONDARY"))

        // Group secondary mappings by scope
        const byScope = new Map<string, UserMapping[]>()
        for (const m of secondaryScopeMappings) {
          if (!byScope.has(m.primitive_scope_id))
            byScope.set(m.primitive_scope_id, [])
          byScope.get(m.primitive_scope_id)!.push(m)
        }

        for (const [secScopeId, secMappings] of byScope) {
          // Find the relation mapping that connects primary → this secondary scope
          const relMapping = relationMappings.find(
            (m) => m.related_scope_id === secScopeId,
          )
          if (!relMapping) continue

          // Build a map: secondary record ID → CSV row indices
          const secRecordRows = new Map<string, number[]>()
          for (const a of anchored) {
            const rowIndices = [
              a.row_index,
              ...(a.additional_row_indices ?? []),
            ].filter((i): i is number => i !== null)
            for (const ri of rowIndices) {
              const relVal = (rows[ri]?.[relMapping.column_index] ?? "").trim()
              if (!relVal) continue
              const lookup = scopeLookups.get(secScopeId)
              const secRecordId =
                lookup?.get(relVal) ??
                lookup?.get(relVal.toLowerCase()) ??
                secondaryNewRecords.get(secScopeId)?.get(relVal.toLowerCase())
              if (!secRecordId) continue
              if (!secRecordRows.has(secRecordId))
                secRecordRows.set(secRecordId, [])
              secRecordRows.get(secRecordId)!.push(ri)
            }
          }

          // Extract scalar values for each secondary record
          for (const [secRecordId, rowIdxs] of secRecordRows) {
            for (const m of secMappings) {
              const rawValues = rowIdxs
                .map((ri) => (rows[ri]?.[m.column_index] ?? "").trim())
                .filter((v) => v !== "")
              if (rawValues.length === 0) continue

              const unique = [...new Set(rawValues)]
              const value = unique[0]
              const conflictValues =
                unique.length > 1 ? unique.slice(1) : undefined

              let normalized = value
              if (m.primitive_type === "number") {
                normalized = value.replace(/,/g, ".")
              }

              extractions.push({
                record_id: secRecordId,
                primitive_id: m.primitive_id,
                primitive_scope_id: m.primitive_scope_id,
                primitive_title: m.primitive_title,
                primitive_type: m.primitive_type,
                value: normalized,
                raw_value: value !== normalized ? value : null,
                needs_cleaning: false,
                confidence: 0.9,
                extraction_type: "explicit",
                related_scope_id: null,
                accepted: true,
                conflict_values: conflictValues,
              })
            }
          }
        }
      }

      // Build secondary anchored records for new records
      const secondaryAnchored: AnchoredRecord[] = []
      for (const [secScopeId, newMap] of secondaryNewRecords) {
        for (const [, provisionalId] of newMap) {
          // Extract PK value from the provisional ID
          const pkValue = provisionalId.replace(`new::${secScopeId}::`, "")
          secondaryAnchored.push({
            record_id: provisionalId,
            pk_value: pkValue,
            name_value: null,
            row_index: null,
            is_new: true,
          })
        }
      }

      // --- Helper extractions (virtual formula columns) ---
      // Helpers are user-authored formulas that compute per-row values from
      // primitives + document cells.  They don't persist to the DB but DO
      // appear in the diff view and, for export flows, the downloaded file.
      appendHelperExtractions({
        mappings,
        anchored,
        cachedPage,
        rows: activeSheet.rows,
        headers: activeSheet.headers,
        extractions,
        translate,
      })

      onDone(extractions, [...anchored, ...secondaryAnchored])
    })()
  }, [])

  return (
    <Spinner
      label={progress || translate("QMS/INGESTION/PROGRESS_EXTRACTING")}
    />
  )
}

// ---------------------------------------------------------------------------
// Single-Record Extraction Step — aggregate all rows into one record
// Numbers: sum. Strings: concat with ",". Relations: exact PK lookup (frontend).
// ---------------------------------------------------------------------------

function SingleRecordExtractionStep({
  scopeId,
  activeSheet,
  mappings,
  recordId,
  onDone,
}: {
  scopeId: string
  activeSheet: ParsedSheet | null
  mappings: UserMapping[]
  recordId: string
  onDone: (extractions: ExtractionEntry[], anchored: AnchoredRecord[]) => void
  onError: (msg: string) => void
}) {
  const { translate } = useTranslation()
  const [progress, setProgress] = useState("")
  const qc = useQueryClient()
  const started = useRef(false)

  useEffect(() => {
    if (started.current) return
    started.current = true
    ;(async () => {
      if (!activeSheet) {
        onDone([], [])
        return
      }

      // Synthesize anchor: all rows map to this single record
      const totalRows = activeSheet.rows.length
      const additionalRows = Array.from(
        { length: Math.max(0, totalRows - 1) },
        (_, i) => i + 1,
      )

      // Look up PK value for this record
      const cached = qc.getQueryData<{
        records: {
          id: string
          values: Record<string, { value: string | null }>
        }[]
        scope: { primitives: { id: string; name: string; is_pk: boolean }[] }
      }>(["records", scopeId])
      const record = cached?.records.find((r) => r.id === recordId)
      const pkPrim = cached?.scope.primitives.find((p) => p.is_pk)
      const pkValue =
        pkPrim && record ? (record.values[pkPrim.name]?.value ?? "") : ""

      const anchored: AnchoredRecord[] = [
        {
          record_id: recordId,
          pk_value: pkValue,
          name_value: null,
          row_index: 0,
          is_new: false,
          additional_row_indices: additionalRows,
        },
      ]
      const rows = activeSheet.rows
      const extractions: ExtractionEntry[] = []

      // Widen cached record values for source/timestamp lookup
      const cachedFull = qc.getQueryData<{
        records: {
          id: string
          values: Record<
            string,
            { value: string | null; source: string; updated_at?: string }
          >
        }[]
        scope: { primitives: { id: string; name: string; is_pk: boolean }[] }
      }>(["records", scopeId])
      const recFull = cachedFull?.records.find((r) => r.id === recordId)

      // Split mappings into scalar vs relation
      const scalarMappings = mappings.filter(
        (m) => m.primitive_type !== "relation",
      )
      const relationMappings = mappings.filter(
        (m) => m.primitive_type === "relation",
      )

      // --- Scalar aggregation (frontend) ---
      setProgress(translate("QMS/INGESTION/PROGRESS_AGGREGATING"))
      for (const m of scalarMappings) {
        const rawValues = rows
          .map((row) => (row[m.column_index] ?? "").trim())
          .filter((v) => v !== "")

        if (rawValues.length === 0) continue

        let aggregated: string

        if (m.primitive_type === "number") {
          const nums = rawValues.map((v) => parseFloat(v.replace(/,/g, ".")))
          if (nums.some((n) => isNaN(n))) {
            aggregated = rawValues.join(", ")
          } else {
            aggregated = String(nums.reduce((a, b) => a + b, 0))
          }
        } else {
          aggregated = rawValues.join(", ")
        }

        // Look up existing DB value for source/timestamp
        const primName = cachedFull?.scope.primitives.find(
          (p) => p.id === m.primitive_id,
        )?.name
        const existCell = primName ? recFull?.values[primName] : undefined

        extractions.push({
          record_id: recordId,
          primitive_id: m.primitive_id,
          primitive_scope_id: m.primitive_scope_id,
          primitive_title: m.primitive_title,
          primitive_type: m.primitive_type,
          value: aggregated,
          raw_value: null,
          needs_cleaning: false,
          confidence: 1,
          extraction_type: "explicit",
          related_scope_id: null,
          accepted: true,
          existing_value: existCell?.value ?? undefined,
          existing_source: existCell?.source,
          existing_updated_at: existCell?.updated_at,
        })
      }

      // --- Relation resolution (frontend, exact PK lookup) ---
      if (relationMappings.length > 0) {
        setProgress(translate("QMS/INGESTION/PROGRESS_LINKS"))

        // Build PK→recordId lookup for each related scope
        const scopeLookups = new Map<string, Map<string, string>>()
        for (const m of relationMappings) {
          const relScopeId = m.related_scope_id
          if (!relScopeId || scopeLookups.has(relScopeId)) continue
          try {
            const cached =
              qc.getQueryData<RecordsPage>(["records", relScopeId]) ??
              (await qc.fetchQuery({
                queryKey: ["records", relScopeId],
                queryFn: () => api.listRecords(relScopeId),
              }))
            if (!cached) continue
            const pkPrim = cached.scope.primitives.find((p) => p.is_pk)
            if (!pkPrim) continue
            const lookup = new Map<string, string>()
            for (const rec of cached.records) {
              const pkVal = rec.values[pkPrim.name]?.value
              if (pkVal != null) {
                lookup.set(pkVal.trim(), rec.id)
                lookup.set(pkVal.trim().toLowerCase(), rec.id)
              }
            }
            scopeLookups.set(relScopeId, lookup)
          } catch (e) {
            console.warn(`Failed to fetch related scope ${relScopeId}:`, e)
          }
        }

        // Single-record mode: collect targets from ALL rows
        for (const m of relationMappings) {
          const relScopeId = m.related_scope_id
          if (!relScopeId) continue
          const lookup = scopeLookups.get(relScopeId)
          if (!lookup) continue

          const rawValues = new Set<string>()
          for (const row of rows) {
            const cell = (row[m.column_index] ?? "").trim()
            if (cell === "") continue
            for (const part of cell.split(",")) {
              const v = part.trim()
              if (v !== "") rawValues.add(v)
            }
          }

          const resolvedTargets = new Set<string>()
          let hasUnresolved = false

          for (const raw of rawValues) {
            const targetId = lookup.get(raw) ?? lookup.get(raw.toLowerCase())
            if (targetId) {
              resolvedTargets.add(targetId)
            } else {
              hasUnresolved = true
            }
          }

          for (const targetId of resolvedTargets) {
            extractions.push({
              record_id: recordId,
              primitive_id: m.primitive_id,
              primitive_scope_id: m.primitive_scope_id,
              primitive_title: m.primitive_title,
              primitive_type: "relation",
              value: targetId,
              raw_value: null,
              needs_cleaning: false,
              confidence: 1,
              extraction_type: "explicit",
              related_scope_id: relScopeId,
              accepted: true,
              mapped_id: m.is_mapping_title
                ? ([...rawValues].find((raw) => {
                    const tid = lookup.get(raw) ?? lookup.get(raw.toLowerCase())
                    return tid === targetId
                  }) ?? null)
                : null,
            })
          }

          if (resolvedTargets.size === 0 && hasUnresolved) {
            extractions.push({
              record_id: recordId,
              primitive_id: m.primitive_id,
              primitive_scope_id: m.primitive_scope_id,
              primitive_title: m.primitive_title,
              primitive_type: "relation",
              value: null,
              raw_value: [...rawValues].join(", "),
              needs_cleaning: true,
              confidence: 0.5,
              extraction_type: "explicit",
              related_scope_id: relScopeId,
              accepted: true,
            })
          }

          // Document has no values for this relation — emit empty marker so
          // diff logic can detect removals of existing DB links
          if (rawValues.size === 0 && resolvedTargets.size === 0) {
            extractions.push({
              record_id: recordId,
              primitive_id: m.primitive_id,
              primitive_scope_id: m.primitive_scope_id,
              primitive_title: m.primitive_title,
              primitive_type: "relation",
              value: null,
              raw_value: null,
              needs_cleaning: false,
              confidence: 1,
              extraction_type: "explicit",
              related_scope_id: relScopeId,
              accepted: true,
            })
          }
        }
      }

      onDone(extractions, anchored)
    })()
  }, [])

  return (
    <Spinner
      label={progress || translate("QMS/INGESTION/PROGRESS_AGGREGATING")}
    />
  )
}

// ---------------------------------------------------------------------------
// Export Extraction Run Step — uses export/extract endpoint to get DB↔doc diffs
// ---------------------------------------------------------------------------

function ExportExtractionRunStep({
  scopeId,
  activeSheet,
  mappings,
  pkPrimitiveId,
  singleRecordId,
  anchorChoice,
  onDone,
  onError,
}: {
  scopeId: string
  activeSheet: ParsedSheet | null
  mappings: UserMapping[]
  pkPrimitiveId: string | null
  singleRecordId?: string
  anchorChoice?: AnchorChoice
  onDone: (extractions: ExtractionEntry[], anchored: AnchoredRecord[]) => void
  onError: (msg: string) => void
}) {
  const { translate } = useTranslation()
  const [progress, setProgress] = useState("")
  const qc = useQueryClient()
  const started = useRef(false)

  useEffect(() => {
    if (started.current) return
    started.current = true
    ;(async () => {
      if (!activeSheet || mappings.length === 0) {
        onDone([], [])
        return
      }

      try {
        // --- Run anchor first using user-confirmed PK column ---
        setProgress(translate("QMS/INGESTION/PROGRESS_ROWS"))
        let anchored: AnchoredRecord[]

        if (singleRecordId) {
          // Single-record mode: all rows map to one record
          const totalRows = activeSheet.rows.length
          const additionalRows = Array.from(
            { length: Math.max(0, totalRows - 1) },
            (_, i) => i + 1,
          )
          const cached =
            qc.getQueryData<{
              records: {
                id: string
                values: Record<string, { value: string | null }>
              }[]
              scope: {
                primitives: { id: string; name: string; is_pk: boolean }[]
              }
            }>(["records", scopeId]) ??
            (await qc.fetchQuery({
              queryKey: ["records", scopeId],
              queryFn: () => api.listRecords(scopeId),
            }))
          const record = cached?.records.find((r) => r.id === singleRecordId)
          const pkPrim = cached?.scope.primitives.find((p) => p.is_pk)
          const pkValue =
            pkPrim && record ? (record.values[pkPrim.name]?.value ?? "") : ""
          anchored = [
            {
              record_id: singleRecordId,
              pk_value: pkValue,
              name_value: null,
              row_index: 0,
              is_new: false,
              additional_row_indices: additionalRows,
            },
          ]
        } else {
          const pkMapping = pkPrimitiveId
            ? mappings.find((m) => m.primitive_id === pkPrimitiveId)
            : null
          const pkColOverride = pkMapping ? pkMapping.column_index : null
          const anchorPayload =
            anchorChoice?.mode === "mapping" && anchorChoice.mappingPrimitiveId
              ? {
                  file_type: "excel" as const,
                  rows: activeSheet.rows,
                  headers: activeSheet.headers,
                  anchor_mode: "mapping" as const,
                  mapping_primitive_id: anchorChoice.mappingPrimitiveId,
                  mapping_col_override:
                    anchorChoice.mappingColumnIndex >= 0
                      ? anchorChoice.mappingColumnIndex
                      : null,
                }
              : {
                  file_type: "excel" as const,
                  rows: activeSheet.rows,
                  headers: activeSheet.headers,
                  pk_col_override: pkColOverride,
                }
          let anchorOut
          try {
            anchorOut = await api.ingestAnchor(scopeId, anchorPayload)
          } catch (e) {
            onError(formatAnchorError(e, translate))
            return
          }
          // Build anchored records (export only uses existing records, not new).
          // In mapping-anchor mode, carry the pair's related record id so the
          // export can resolve mapping-title columns to the specific edge.
          anchored = anchorOut.matches.map((m) => ({
            record_id: m.record_id,
            pk_value: m.pk_value,
            name_value: m.name_value,
            row_index: m.row_index,
            is_new: false,
            additional_row_indices: [],
            related_record_id: m.mapped_to_record_id ?? null,
          }))
        }

        if (anchored.length === 0) {
          onDone([], [])
          return
        }

        setProgress(translate("QMS/INGESTION/PROGRESS_COMPARE"))
        const result = await api.exportExtract(scopeId, {
          mappings: mappings.map((m) => ({
            primitive_id: m.primitive_id,
            primitive_scope_id: m.primitive_scope_id,
            column_index: m.column_index,
            rollup: m.isRollup ?? false,
            is_mapping_title: m.is_mapping_title ?? false,
          })),
          anchored_records: anchored
            .filter((a) => !a.is_new)
            .map((a) => ({
              record_id: a.record_id,
              row_index: a.row_index,
              additional_row_indices: a.additional_row_indices ?? [],
              related_record_id: a.related_record_id ?? null,
            })),
          rows: activeSheet.rows,
          headers: activeSheet.headers,
        })

        // Convert export diffs to ExtractionEntry format
        // In export: value = doc cell value (existing), existing_value = db value (proposed)
        // Relation primitives are treated as scalar strings in export (PK values written to CSV)
        const extractions: ExtractionEntry[] = result.diffs.map((diff) => {
          const m = mappings.find((x) => x.primitive_id === diff.primitive_id)
          const ptype =
            m?.primitive_type === "relation"
              ? "string"
              : (m?.primitive_type ?? "string")
          return {
            record_id: diff.record_id,
            primitive_id: diff.primitive_id,
            primitive_scope_id: diff.primitive_scope_id,
            primitive_title: m?.primitive_title ?? diff.primitive_id,
            primitive_type: ptype,
            value: diff.doc_value, // doc cell value (will be placed in "existing_value" slot by buildDiffState)
            raw_value: null,
            needs_cleaning: false,
            confidence: 0.9,
            extraction_type: "explicit",
            existing_value: diff.db_value, // DB value (will be the "proposed" change)
            existing_source: diff.db_source ?? undefined,
            existing_updated_at: diff.db_updated_at ?? undefined,
            accepted: true,
            export_column_index: diff.column_index, // preserve for file reconstruction
          }
        })

        // Helpers: compute virtual formula columns per anchored record and
        // emit them as extraction entries.  The diff view renders them; the
        // download path appends them as extra columns.
        const cachedPage =
          qc.getQueryData<{
            records: {
              id: string
              values: Record<
                string,
                { value: string | null; source: string; updated_at?: string }
              >
            }[]
            scope: { primitives: { id: string; name: string }[] }
          }>(["records", scopeId]) ??
          (await qc.fetchQuery({
            queryKey: ["records", scopeId],
            queryFn: () => api.listRecords(scopeId),
          }))
        appendHelperExtractions({
          mappings,
          anchored,
          cachedPage,
          rows: activeSheet.rows,
          headers: activeSheet.headers,
          extractions,
          translate,
        })

        onDone(extractions, anchored)
      } catch (e) {
        onError(
          translate("QMS/INGESTION/EXPORT_EXTRACT_FAILED", {
            error: String(e),
          }),
        )
      }
    })()
  }, [])

  return (
    <Spinner label={progress || translate("QMS/INGESTION/PROGRESS_COMPARE")} />
  )
}

// ---------------------------------------------------------------------------
// Review Step — changed values only, mini pivot table, scope pills, inline commit
// ---------------------------------------------------------------------------

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function ReviewStep({
  scopeId,
  anchored,
  extractions,
  pkPrimitiveId,
  onChange,
  onCommit,
}: {
  scopeId: string
  anchored: AnchoredRecord[]
  extractions: ExtractionEntry[]
  pkPrimitiveId: string | null
  onChange: (updated: ExtractionEntry[]) => void
  onCommit: (final: ExtractionEntry[]) => void
}) {
  const { translate } = useTranslation()
  const [committing, setCommitting] = useState(false)

  // Filter to entries with actual new / changed values, plus unresolved relation
  // entries (shown as informational — not committable, but visible to the user)
  const changed = extractions.filter(
    (e) =>
      (e.primitive_type === "relation" && e.needs_cleaning) ||
      (e.value !== null &&
        e.value !== "" &&
        (e.existing_value === undefined ||
          e.existing_value === null ||
          e.value !== e.existing_value)),
  )

  const scopeIds = [...new Set(changed.map((e) => e.primitive_scope_id))]
  const [activeScopeId, setActiveScopeId] = useState(() =>
    scopeIds.includes(scopeId) ? scopeId : (scopeIds[0] ?? scopeId),
  )

  // Primitives (columns) for active scope in changed set
  const scopeChanged = changed.filter(
    (e) => e.primitive_scope_id === activeScopeId,
  )
  const primitiveIds = [...new Set(scopeChanged.map((e) => e.primitive_id))]
  const primitives = primitiveIds.map((id) => {
    const e = scopeChanged.find((x) => x.primitive_id === id)!
    return { id, title: e.primitive_title, type: e.primitive_type }
  })

  // Records that have changes in this scope
  const affectedRecordIds = [...new Set(scopeChanged.map((e) => e.record_id))]
  const affectedRecords = anchored.filter((a) =>
    affectedRecordIds.includes(a.record_id),
  )

  const updateEntry = (
    recordId: string,
    primitiveId: string,
    patch: Partial<ExtractionEntry>,
  ) => {
    onChange(
      extractions.map((e) =>
        e.record_id === recordId && e.primitive_id === primitiveId
          ? { ...e, ...patch }
          : e,
      ),
    )
  }

  // Row-level selection: check if ALL committable entries for a record are accepted
  const isRowSelected = (recordId: string) => {
    const rowEntries = scopeChanged.filter(
      (e) => e.record_id === recordId && e.value !== null && e.value !== "",
    )
    return rowEntries.length > 0 && rowEntries.every((e) => e.accepted)
  }
  const isRowPartial = (recordId: string) => {
    const rowEntries = scopeChanged.filter(
      (e) => e.record_id === recordId && e.value !== null && e.value !== "",
    )
    return (
      rowEntries.some((e) => e.accepted) && !rowEntries.every((e) => e.accepted)
    )
  }
  const toggleRow = (recordId: string) => {
    const selected = isRowSelected(recordId)
    onChange(
      extractions.map((e) =>
        e.record_id === recordId && e.primitive_scope_id === activeScopeId
          ? { ...e, accepted: !selected }
          : e,
      ),
    )
  }
  const allRowsSelected =
    affectedRecords.length > 0 &&
    affectedRecords.every((r) => isRowSelected(r.record_id))
  const someRowsSelected = affectedRecords.some(
    (r) => isRowSelected(r.record_id) || isRowPartial(r.record_id),
  )
  const toggleAllRows = () => {
    const newVal = !allRowsSelected
    const recordSet = new Set(affectedRecordIds)
    onChange(
      extractions.map((e) =>
        recordSet.has(e.record_id) && e.primitive_scope_id === activeScopeId
          ? { ...e, accepted: newVal }
          : e,
      ),
    )
  }

  const acceptedCount = extractions.filter(
    (e) =>
      e.accepted &&
      e.value !== null &&
      e.value !== "" &&
      (e.existing_value === undefined ||
        e.existing_value === null ||
        e.value !== e.existing_value),
  ).length

  const conflictCount = changed.filter(
    (e) =>
      e.existing_value !== undefined &&
      e.existing_value !== null &&
      e.existing_source &&
      e.existing_source !== "manual",
  ).length

  // Duplicate-row value conflicts (same PK, different values across rows)
  const duplicateConflicts = extractions.filter(
    (e) =>
      (e.conflict_values?.length ?? 0) > 0 && e.primitive_type !== "relation",
  )
  const [showDupConflicts, setShowDupConflicts] = useState(
    duplicateConflicts.length > 0,
  )

  async function handleCommit() {
    setCommitting(true)
    try {
      await onCommit(extractions)
    } finally {
      setCommitting(false)
    }
  }

  if (changed.length === 0) {
    return (
      <div className="flex flex-col items-center gap-3 py-12">
        <p className="text-sm text-grey5">
          {translate("QMS/INGESTION/NO_VALUES")}
        </p>
        <button
          onClick={() => onCommit(extractions)}
          className="rounded-lg border border-grey4 px-4 py-2 text-sm text-grey5 hover:bg-grey1"
        >
          {translate("QMS/COMMON/CLOSE")}
        </button>
      </div>
    )
  }

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <div>
          <p className="text-sm text-grey5">
            {translate("QMS/INGESTION/FIELDS_SHOWN")}
          </p>
          {conflictCount > 0 && (
            <p className="mt-0.5 text-xs text-warning">
              {translate("QMS/INGESTION/FIELD_CONFLICT_COUNT", {
                count: conflictCount,
              })}
            </p>
          )}
          {duplicateConflicts.length > 0 && (
            <p className="mt-0.5 text-xs text-warning">
              {translate("QMS/INGESTION/CONFLICTS_EXIST", {
                count: duplicateConflicts.length,
              })}
            </p>
          )}
        </div>
        <span className="ml-4 whitespace-nowrap text-xs text-grey5">
          {translate("QMS/INGESTION/CHANGES_TO_WRITE", {
            count: acceptedCount,
          })}
        </span>
      </div>

      {/* Duplicate-row conflicts panel */}
      {duplicateConflicts.length > 0 && (
        <div className="mb-4 overflow-hidden rounded-lg border border-warning bg-warning/10">
          <button
            className="flex w-full items-center justify-between px-4 py-2.5 text-left"
            onClick={() => setShowDupConflicts((s) => !s)}
          >
            <span className="text-xs font-semibold text-warning">
              {translate("QMS/INGESTION/DUPLICATE_ROW_CONFLICT", {
                count: duplicateConflicts.length,
              })}
            </span>
            <span className="text-xs text-warning">
              {showDupConflicts ? "▲" : "▼"}
            </span>
          </button>
          {showDupConflicts && (
            <div className="divide-y divide-warning border-t border-warning">
              {duplicateConflicts.map((entry) => {
                const rec = anchored.find(
                  (a) => a.record_id === entry.record_id,
                )
                const allValues = [
                  entry.value,
                  ...(entry.conflict_values ?? []),
                ].filter((v): v is string => v !== null && v !== "")
                return (
                  <div
                    key={`${entry.record_id}::${entry.primitive_id}`}
                    className="bg-grey2/70 px-4 py-3"
                  >
                    <div className="mb-2 flex items-baseline gap-2">
                      <span className="text-xs font-medium text-grey6">
                        {rec?.pk_value ?? entry.record_id}
                      </span>
                      <span className="text-[10px] text-grey5">→</span>
                      <span className="text-xs text-grey5">
                        {entry.primitive_title}
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      {allValues.map((v, i) => (
                        <button
                          key={i}
                          onClick={() =>
                            updateEntry(entry.record_id, entry.primitive_id, {
                              value: v,
                            })
                          }
                          className={clsx(
                            "rounded-full border px-2.5 py-1 text-xs transition-colors",
                            entry.value === v
                              ? "border-yellow1 bg-yellow1 text-black"
                              : "border-grey4 bg-grey2 text-grey6 hover:border-yellow1",
                          )}
                        >
                          {v}
                        </button>
                      ))}
                      <input
                        type="text"
                        value={entry.value ?? ""}
                        onChange={(e) =>
                          updateEntry(entry.record_id, entry.primitive_id, {
                            value: e.target.value,
                            conflict_values: entry.conflict_values,
                          })
                        }
                        placeholder={translate(
                          "QMS/INGESTION/MERGE_PLACEHOLDER",
                        )}
                        className="min-w-[140px] rounded border border-grey4 bg-grey2 px-2 py-1 text-xs text-grey6 focus:border-yellow1 focus:outline-hidden"
                      />
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      {/* Scope pills */}
      <ScopePills
        scopeIds={scopeIds}
        active={activeScopeId}
        onChange={setActiveScopeId}
      />

      {/* Mini pivot table */}
      {primitives.length === 0 ? (
        <p className="py-8 text-center text-sm text-grey5">
          {translate("QMS/INGESTION/NO_CHANGES_SCOPE")}
        </p>
      ) : (
        <div className="mb-5 overflow-x-auto rounded-lg border">
          <table className="min-w-full border-collapse text-sm">
            <thead>
              <tr className="border-b bg-grey1">
                <th className="sticky left-0 z-10 whitespace-nowrap border-r bg-grey1 px-3 py-2 text-left text-xs font-medium text-grey5">
                  <label className="flex cursor-pointer items-center gap-2">
                    <input
                      type="checkbox"
                      checked={allRowsSelected}
                      ref={(el) => {
                        if (el)
                          el.indeterminate =
                            !allRowsSelected && someRowsSelected
                      }}
                      onChange={toggleAllRows}
                      className="accent-yellow1"
                    />
                    {translate("QMS/INGESTION/RECORD_LABEL")}
                  </label>
                </th>
                {primitives.map((p) => (
                  <th
                    key={p.id}
                    className="whitespace-nowrap border-r px-3 py-2 text-left text-xs font-medium text-grey5 last:border-r-0"
                  >
                    <div className="flex items-center gap-1">
                      <span className="font-mono text-[9px] text-grey5">
                        [{p.type}]
                      </span>
                      {p.title}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y">
              {affectedRecords.map((rec) => {
                const rowOn = isRowSelected(rec.record_id)
                const rowPartial = isRowPartial(rec.record_id)
                return (
                  <tr
                    key={rec.record_id}
                    className={clsx(
                      "transition-opacity hover:bg-grey3/50",
                      !rowOn && !rowPartial && "opacity-40",
                    )}
                  >
                    {/* Row header with row-level checkbox */}
                    <td className="sticky left-0 whitespace-nowrap border-r bg-grey2 px-3 py-2">
                      <label className="flex cursor-pointer items-center gap-2">
                        <input
                          type="checkbox"
                          checked={rowOn}
                          ref={(el) => {
                            if (el) el.indeterminate = rowPartial
                          }}
                          onChange={() => toggleRow(rec.record_id)}
                          className="accent-yellow1"
                        />
                        <span className="text-xs font-medium text-grey6">
                          {rec.pk_value}
                        </span>
                        {rec.name_value && rec.name_value !== rec.pk_value && (
                          <span className="ml-1 text-[10px] text-grey5">
                            {rec.name_value}
                          </span>
                        )}
                      </label>
                    </td>
                    {/* Value cells */}
                    {primitives.map((p) => {
                      const entry = extractions.find(
                        (e) =>
                          e.record_id === rec.record_id &&
                          e.primitive_id === p.id,
                      )
                      if (!entry) {
                        return (
                          <td
                            key={p.id}
                            className="border-r px-3 py-2 text-xs text-grey5 last:border-r-0"
                          >
                            —
                          </td>
                        )
                      }
                      // Relation column with null value: show a badge instead of a dash
                      if (
                        p.type === "relation" &&
                        (entry.value === null || entry.value === "")
                      ) {
                        return (
                          <td
                            key={p.id}
                            className="border-r px-3 py-2 last:border-r-0"
                          >
                            {entry.raw_value ? (
                              <span
                                className="inline-flex items-center gap-1 rounded border border-warning bg-warning/10 px-1.5 py-0.5 text-[10px] text-warning"
                                title={translate(
                                  "QMS/INGESTION/RELATION_RAW_VALUE_NO_MATCH",
                                  { value: entry.raw_value },
                                )}
                              >
                                <span className="max-w-[80px] truncate">
                                  {entry.raw_value}
                                </span>
                                <span className="shrink-0 font-medium">
                                  · {translate("QMS/INGESTION/NEW_RECORD")}
                                </span>
                              </span>
                            ) : (
                              <span className="inline-flex items-center rounded border border-grey4 bg-grey3 px-1.5 py-0.5 text-[10px] text-grey5">
                                {translate("QMS/INGESTION/NO_VALUE_FOUND")}
                              </span>
                            )}
                          </td>
                        )
                      }
                      if (
                        entry.value === null ||
                        entry.value === "" ||
                        (entry.existing_value !== undefined &&
                          entry.existing_value !== null &&
                          entry.value === entry.existing_value)
                      ) {
                        return (
                          <td
                            key={p.id}
                            className="border-r px-3 py-2 text-xs text-grey5 last:border-r-0"
                          >
                            —
                          </td>
                        )
                      }
                      const isConflict = !!(
                        entry.existing_value !== undefined &&
                        entry.existing_value !== null &&
                        entry.existing_source &&
                        entry.existing_source !== "manual"
                      )
                      return (
                        <td
                          key={p.id}
                          className="min-w-[120px] border-r px-3 py-2 last:border-r-0"
                        >
                          <ReviewCell
                            entry={entry}
                            isConflict={isConflict}
                            isPk={
                              pkPrimitiveId != null && p.id === pkPrimitiveId
                            }
                            onChange={(val) =>
                              updateEntry(rec.record_id, p.id, { value: val })
                            }
                            onToggle={() =>
                              updateEntry(rec.record_id, p.id, {
                                accepted: !entry.accepted,
                              })
                            }
                            onKeepExisting={() =>
                              updateEntry(rec.record_id, p.id, {
                                value: entry.existing_value ?? null,
                                accepted: true,
                              })
                            }
                            primitiveType={p.type}
                            relatedScopeId={entry.related_scope_id ?? undefined}
                          />
                        </td>
                      )
                    })}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex justify-end">
        <button
          onClick={handleCommit}
          disabled={committing}
          className="flex items-center gap-2 rounded-lg bg-yellow1 px-5 py-2 text-sm font-medium text-black transition-colors hover:bg-yellow5/30 disabled:opacity-40"
        >
          {committing && (
            <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
          )}
          {committing
            ? translate("QMS/INGESTION/COMMITTING")
            : acceptedCount === 0
              ? translate("QMS/INGESTION/DONE")
              : translate("QMS/INGESTION/COMMIT_CHANGES", {
                  count: acceptedCount,
                })}
        </button>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Review cell
// ---------------------------------------------------------------------------

function ReviewCell({
  entry,
  isConflict,
  isPk,
  onChange,
  onToggle,
  onKeepExisting,
  primitiveType,
  relatedScopeId,
}: {
  entry: ExtractionEntry
  isConflict: boolean
  isPk?: boolean
  onChange: (v: string) => void
  onToggle: () => void
  onKeepExisting: () => void
  primitiveType?: string
  relatedScopeId?: string
}) {
  const { translate } = useTranslation()
  // Relation cells: value is a target record ID (UUID). Display raw_value (the
  // original document text that was matched) with a resolved/unresolved badge.
  if (primitiveType === "relation") {
    const label = entry.raw_value ?? entry.value ?? "—"
    const resolved = entry.value !== null && !entry.needs_cleaning
    const hint = relatedScopeId
      ? translate("QMS/INGESTION/NO_MATCH_FOUND", { scope: relatedScopeId })
      : translate("QMS/INGESTION/NO_MATCH_GENERIC")
    return (
      <div
        className={clsx(
          "flex flex-col gap-0.5",
          !entry.accepted && "opacity-40",
        )}
      >
        <div className="flex items-center gap-1">
          <input
            type="checkbox"
            checked={entry.accepted && resolved}
            onChange={onToggle}
            disabled={!resolved}
            className="shrink-0 accent-yellow1"
            title={resolved ? translate("QMS/INGESTION/INCLUDE_LINK") : hint}
          />
          <span
            className="font-mono max-w-[100px] truncate text-xs text-grey6"
            title={label}
          >
            {label}
          </span>
          {resolved ? (
            <span className="shrink-0 rounded bg-purple1/20 px-1 text-[10px] text-purple1">
              {translate("QMS/INGESTION/LINKED_BADGE")}
            </span>
          ) : (
            <span className="shrink-0 rounded bg-warning/20 px-1 text-[10px] text-warning">
              {translate("QMS/INGESTION/UNRESOLVED_BADGE")}
            </span>
          )}
        </div>
        {!resolved && relatedScopeId && (
          <p className="ml-4 text-[10px] leading-tight text-warning">
            {translate("QMS/INGESTION/UPLOAD_TO_CREATE", {
              scope: relatedScopeId,
            })}
          </p>
        )}
      </div>
    )
  }

  return (
    <div className={clsx(!entry.accepted && "opacity-40")}>
      <div className="flex items-center gap-1">
        {!isPk && (
          <input
            type="checkbox"
            checked={entry.accepted}
            onChange={onToggle}
            className="shrink-0 accent-yellow1"
            title={translate("QMS/INGESTION/INCLUDE_CHANGE")}
          />
        )}
        <input
          type="text"
          value={entry.value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          readOnly={isPk}
          className={clsx(
            "min-w-0 flex-1 rounded border px-1.5 py-1 text-xs",
            entry.needs_cleaning
              ? "border-warning bg-warning/10"
              : isConflict
                ? "border-warning bg-warning/10"
                : "border-success/30 bg-success/10",
          )}
        />
        {entry.extraction_type === "implicit" && (
          <span className="shrink-0 rounded bg-purple1/20 px-1 text-[10px] text-purple1">
            AI
          </span>
        )}
      </div>
      {isConflict && (
        <div className="ml-4 mt-0.5 flex items-center gap-1 text-[10px] text-warning">
          <span
            className="max-w-[80px] truncate"
            title={entry.existing_value ?? ""}
          >
            {translate("QMS/INGESTION/WAS_VALUE", {
              value: entry.existing_value ?? "",
            })}
          </span>
          <button
            onClick={onKeepExisting}
            className="whitespace-nowrap underline hover:text-warning"
          >
            {translate("QMS/INGESTION/KEEP_EXISTING")}
          </button>
        </div>
      )}
      {entry.reasoning && (
        <details className="ml-4 mt-0.5">
          <summary className="cursor-pointer text-[10px] text-grey5">
            {translate("QMS/INGESTION/REASON")}
          </summary>
          <p className="mt-0.5 max-w-[200px] text-[10px] text-grey5">
            {entry.reasoning}
          </p>
        </details>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Cascade Step
// ---------------------------------------------------------------------------

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function CascadeStep({
  committedScopeId,
  remainingTargetScopeIds,
  colsByScope,
  pendingRelationsByScopeId,
  onCascade,
  onDone,
}: {
  committedScopeId: string
  remainingTargetScopeIds: string[]
  colsByScope: Record<string, string[]>
  pendingRelationsByScopeId: Record<string, number>
  onCascade: (scopeId: string) => Promise<void>
  onDone: () => void
}) {
  const { translate } = useTranslation()
  const [customScopeId, setCustomScopeId] = useState("")
  const [loading, setLoading] = useState(false)

  const { data: allScopes } = useQuery({
    queryKey: ["scopes"],
    queryFn: () => api.listScopes(),
  })

  // remainingTargetScopeIds and colsByScope are pre-computed by the parent from the
  // accumulated session data — the parent accumulates these across all cascade steps,
  // so the list is never truncated when anchorDetected changes.
  const detectedScopeIds = remainingTargetScopeIds

  const otherScopes = (allScopes ?? []).filter(
    (s) => s.id !== committedScopeId && !remainingTargetScopeIds.includes(s.id),
  )

  async function handleCascade(scopeId: string) {
    setLoading(true)
    try {
      await onCascade(scopeId)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="space-y-6">
      <div className="pb-2 text-center">
        <div className="mb-3 inline-flex h-10 w-10 items-center justify-center rounded-full bg-success/20">
          <span className="text-lg text-success">✓</span>
        </div>
        <h3 className="text-sm font-semibold text-white">
          {translate("QMS/INGESTION/COMMITTED_IN", { scope: committedScopeId })}
        </h3>
        <p className="mt-1 text-xs text-grey5">
          {translate("QMS/INGESTION/OTHER_SCOPES_FOUND")}
        </p>
      </div>

      {detectedScopeIds.length > 0 && (
        <div>
          <p className="mb-2 text-xs font-medium text-grey6">
            {translate("QMS/INGESTION/OTHER_SCOPES_DETECTED")}
          </p>
          <div className="space-y-2">
            {detectedScopeIds.map((sid) => {
              const pending = pendingRelationsByScopeId[sid] ?? 0
              return (
                <button
                  key={sid}
                  disabled={loading}
                  onClick={() => handleCascade(sid)}
                  className="flex w-full items-center justify-between rounded-lg border px-4 py-3 text-left transition-colors hover:border-yellow5 hover:bg-yellow5/10 disabled:opacity-50"
                >
                  <div>
                    <p className="text-sm font-medium capitalize text-white">
                      {sid}
                    </p>
                    {colsByScope[sid]?.length > 0 && (
                      <p className="mt-0.5 text-xs text-grey5">
                        {colsByScope[sid].join(", ")}
                      </p>
                    )}
                    {pending > 0 && (
                      <p className="mt-0.5 text-xs font-medium text-warning">
                        {translate("QMS/INGESTION/PENDING_LINKS", {
                          count: pending,
                        })}
                      </p>
                    )}
                  </div>
                  <span className="ml-3 whitespace-nowrap text-xs font-medium text-yellow1">
                    {translate("QMS/INGESTION/UPLOAD_BTN")}
                  </span>
                </button>
              )
            })}
          </div>
        </div>
      )}

      {otherScopes.length > 0 && (
        <div>
          <p className="mb-2 text-xs font-medium text-grey6">
            {translate("QMS/INGESTION/OR_NEW_ANCHOR")}
          </p>
          <div className="flex gap-2">
            <select
              value={customScopeId}
              onChange={(e) => setCustomScopeId(e.target.value)}
              disabled={loading}
              className="flex-1 rounded-lg border border-grey4 px-3 py-2 text-sm focus:outline-hidden focus:ring-2 focus:ring-yellow5 disabled:opacity-50"
            >
              <option value="">
                {translate("QMS/INGESTION/SELECT_SCOPE")}
              </option>
              {otherScopes.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
            <button
              disabled={!customScopeId || loading}
              onClick={() => handleCascade(customScopeId)}
              className="rounded-lg bg-yellow1 px-4 py-2 text-sm text-black transition-colors hover:bg-yellow5/30 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {translate("QMS/INGESTION/ANCHOR_BTN")}
            </button>
          </div>
        </div>
      )}

      {Object.values(pendingRelationsByScopeId).some((c) => c > 0) && (
        <div className="rounded border border-warning bg-warning/10 px-3 py-2 text-xs text-warning">
          {translate("QMS/INGESTION/CLOSE_WARNING")}
        </div>
      )}

      <div className="flex justify-end border-t pt-2">
        <button
          onClick={onDone}
          disabled={loading}
          className="px-4 py-2 text-sm text-grey5 hover:text-white disabled:opacity-50"
        >
          {translate("QMS/COMMON/DONE")}
        </button>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Tiny helpers
// ---------------------------------------------------------------------------

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function ScoreBadge({ score }: { score: number }) {
  const pct = Math.round(score * 100)
  return (
    <span
      className={clsx(
        "font-mono rounded px-1.5 py-0.5 text-xs",
        pct >= 90 ? "bg-success/20 text-success" : "bg-warning/20 text-warning",
      )}
    >
      {pct}%
    </span>
  )
}
